// NodeLists.js
import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle, useMemo } from 'react';
import ReactDOM from 'react-dom';
import './App.css';
import useStyles from './sharedStyles'; 
import { useTable, useSortBy, useResizeColumns } from 'react-table';
import { FixedSizeList as VirtualList } from 'react-window';
import { Sparklines, SparklinesLine } from 'react-sparklines';
import { ETHERSCAN, PCAP } from './config';
import { genericTokenIcon, genericUserIcon } from './config';
import Web3 from 'web3';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, List, ListItem, ListItemText, CircularProgress } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Checkbox from '@material-ui/core/Checkbox';
import { FaStar } from 'react-icons/fa';
import { useWatchlist } from './watchlist/WatchlistContext';

const web3 = new Web3();

function normalizeWatchChain(chain) {
  const raw = String(chain || '').trim().toUpperCase();
  if (!raw) return 'ETH';
  if (raw === 'ETHEREUM') return 'ETH';
  if (raw === 'BINANCE' || raw === 'BINANCE SMART CHAIN') return 'BSC';
  return raw;
}

function storyTypeColorHex(storyType) {
  const t = String(storyType || '').trim().toUpperCase();
  if (t === 'FUNNEL')   return '#4d79ff';
  if (t === 'CLUSTER')  return '#9ef7c5';
  if (t === 'LOOP')     return '#ffe55c';
  if (t === 'MIRROR')   return '#b45aff';
  if (t === 'STAGING')  return '#00ffa0';
  if (t === 'HOTLINKS') return '#ff6b6b';
  if (t === 'WHALE')    return '#ffd700';
  if (t === 'SURGE')    return '#00c2ff';
  if (t === 'FLOW')     return '#00c2ff';
  if (t === 'CATEGORY') return '#cfd3ff';
  if (t === 'DISCOVERY')return '#ff8bd4';
  if (t === 'SANDWICH') return '#ffa500';
  if (t === 'THESIS')   return '#cfd3ff';
  return '#4d79ff';
}

const PriceDetailsDialog = ({ onClose }) => {
  return (
    <Dialog open={true} onClose={onClose}>
      <DialogTitle style={{ color: 'white', backgroundColor: '#1a1a1a' }}>Price Calculation Details</DialogTitle>
      <DialogContent style={{ color: 'white', backgroundColor: '#1a1a1a' }}>
        <PriceDetailsContent />
      </DialogContent>
      <DialogActions style={{ backgroundColor: '#1a1a1a' }}>
        <Button variant="contained" color="primary" onClick={onClose}>
          CLOSE
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const FragilityScoreDetailsDialog = ({ onClose }) => {
  return (
    <Dialog open={true} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle style={{ color: 'white', backgroundColor: '#1a1a1a' }}>Fragility Score</DialogTitle>
      <DialogContent style={{ color: 'white', backgroundColor: '#1a1a1a' }}>
        <Typography variant="body1" style={{ color: 'white', marginBottom: 12 }}>
          Fragility Score is a 0-100 estimate of how structurally fragile a token market appears to be.
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText
              primary="Lower scores suggest stronger liquidity and more reliable pricing."
              primaryTypographyProps={{ style: { color: 'white' } }}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Higher scores suggest thinner liquidity, weaker price confidence, or inflated valuation risk."
              primaryTypographyProps={{ style: { color: 'white' } }}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="This metric should be treated as a risk signal, not a certainty."
              primaryTypographyProps={{ style: { color: 'white' } }}
            />
          </ListItem>
        </List>
      </DialogContent>
      <DialogActions style={{ backgroundColor: '#1a1a1a' }}>
        <Button variant="contained" color="primary" onClick={onClose}>
          CLOSE
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PriceDetailsContent = () => {
  return (
    <div>
      <Typography variant="body1" style={{ color: 'white' }}>
        <strong style={{ color: 'white' }}>Note:</strong> Token values are derived from on-chain data.
      </Typography>
      <List>
        <ListItem>
          <ListItemText 
            primary="Prices are updated every five minutes." 
            primaryTypographyProps={{ style: { color: 'white' } }}
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Token valuation is established via pairing with WETH to determine equivalent ETH amounts." 
            primaryTypographyProps={{ style: { color: 'white' } }}
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="These amounts are then adjusted according to current USD price of Ethereum." 
            primaryTypographyProps={{ style: { color: 'white' } }}
          />
        </ListItem>
      </List>
      <Typography variant="body1" style={{ color: 'white' }}>
        To sum it up, the price should be thought of and used as an estimate, and may not match other exchanges exactly. But hey, they don't match each other either. ;-)
      </Typography>
    </div>
  );
};

export function formatPrice(price, decimals = 2) {
  const numericPrice = Number(price);
  if (!Number.isFinite(numericPrice) || numericPrice <= 0 || numericPrice > 100000) {
    return '';
  }

  const formatDecimal = (value, precision) => {
    const s = Number(value).toFixed(precision);
    return s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s;
  };

  const toSubscriptDigits = (value) => {
    const map = {
      '0': '₀',
      '1': '₁',
      '2': '₂',
      '3': '₃',
      '4': '₄',
      '5': '₅',
      '6': '₆',
      '7': '₇',
      '8': '₈',
      '9': '₉',
    };
    return String(value).split('').map((ch) => map[ch] || ch).join('');
  };

  if (numericPrice < 0.01) {
    const fixed = numericPrice.toFixed(18).replace(/0+$/, '').replace(/\.$/, '');
    const decimalIndex = fixed.indexOf('.');
    const fractional = decimalIndex >= 0 ? fixed.slice(decimalIndex + 1) : '';
    const leadingZerosMatch = fractional.match(/^0+/);
    const leadingZeros = leadingZerosMatch ? leadingZerosMatch[0].length : 0;

    if (leadingZeros > 6 && fractional.length > leadingZeros) {
      const significant = fractional.slice(leadingZeros, leadingZeros + 4);
      return `$0.0${toSubscriptDigits(leadingZeros)}${significant}`;
    }

    if (numericPrice < 1 && fractional.length > leadingZeros + 4) {
      return `$0.${fractional.slice(0, leadingZeros + 4)}`;
    }

    return '$' + fixed;
  }

  // For normal prices, keep a readable fixed precision but still trim trailing zeros.
  return '$' + formatDecimal(numericPrice, decimals);
}

function formatMarketCap(marketCap) {
  const n = Number(marketCap);
  if (!Number.isFinite(n)) return '-';
  const formattedMarketCap = Math.round(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
  return '$' + formattedMarketCap;
}

function formatTokenAmount(value) {
  if (value === null || value === undefined || value === '') return '-';
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '-';
  return numericValue.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function formatWholeTokenAmount(value) {
  if (value === null || value === undefined || value === '') return '-';
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '-';
  return Math.round(numericValue).toLocaleString();
}

function formatRoundedDollarAmount(value) {
  if (value === null || value === undefined || value === '') return '-';
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '-';
  return '$' + Math.round(numericValue).toLocaleString();
}

// Compact suffix formatter: $1.2M, $450K, $3.1B
function formatCompact(value, prefix = '$') {
  if (value === null || value === undefined || value === '') return '-';
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e12) return `${sign}${prefix}${(abs / 1e12).toFixed(abs >= 10e12 ? 1 : 2)}T`;
  if (abs >= 1e9)  return `${sign}${prefix}${(abs / 1e9).toFixed(abs >= 10e9 ? 1 : 2)}B`;
  if (abs >= 1e6)  return `${sign}${prefix}${(abs / 1e6).toFixed(abs >= 10e6 ? 1 : 2)}M`;
  if (abs >= 1e3)  return `${sign}${prefix}${(abs / 1e3).toFixed(abs >= 10e3 ? 1 : 1)}K`;
  if (abs === 0)   return `${prefix}0`;
  return `${sign}${prefix}${Math.round(abs).toLocaleString()}`;
}

// Compact token supply: 1.2B, 450K, etc. (no $ prefix)
function formatCompactTokens(value) {
  if (value === null || value === undefined || value === '') return '-';
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return formatCompact(n, '');
}

function formatShortAddress(address) {
  if (address && address.length > 8) {
    const start = address.substring(0, 6); // '0x' + first 4 characters
    const end = address.substring(address.length - 4); // last 4 characters
    return `${start}…${end}`; // Using the ellipsis character directly
  }
  return address;
}

function formatAnomalyFlags(flags) {
  if (!flags) return '';
  if (Array.isArray(flags)) {
    return flags.filter(Boolean).map((flag) => String(flag)).join(', ');
  }
  return String(flags);
}

function formatPriceQualityTooltip(token) {
  if (!token) return '';
  const parts = [];
  if (token.priceSourceDex || token.priceSourceType) {
    parts.push(`Source: ${token.priceSourceDex || '-'}${token.priceSourceType ? ` (${token.priceSourceType})` : ''}`);
  }
  if (token.priceConfidence !== null && token.priceConfidence !== undefined && token.priceConfidence !== '') {
    parts.push(`Confidence: ${token.priceConfidence}`);
  }
  if (token.fragilityScore !== null && token.fragilityScore !== undefined && token.fragilityScore !== '') {
    parts.push(`Fragility: ${token.fragilityScore}`);
  }
  const anomalyText = formatAnomalyFlags(token.anomalyFlags);
  if (anomalyText) {
    parts.push(`Anomalies: ${anomalyText}`);
  }
  return parts.join('\n');
}

function formatLiquidityTooltip(token) {
  if (!token) return '';
  const parts = [];
  if (token.priceSourceDex || token.priceSourceType) {
    parts.push(`Source: ${token.priceSourceDex || '-'}${token.priceSourceType ? ` (${token.priceSourceType})` : ''}`);
  }
  if (token.priceConfidence !== null && token.priceConfidence !== undefined && token.priceConfidence !== '') {
    parts.push(`Confidence: ${token.priceConfidence}`);
  }
  const anomalyText = formatAnomalyFlags(token.anomalyFlags);
  if (anomalyText) {
    parts.push(`Anomalies: ${anomalyText}`);
  }
  return parts.join('\n');
}


// Data quality thresholds for inline badges
const DQ_CONFIDENCE_WARN = 0.7;
const DQ_FRAGILITY_WARN = 0.6;
const DQ_BENIGN_ANOMALY_FLAGS = new Set([
  'market_cap_uses_total_supply',
]);

function normalizeAnomalyFlags(flags) {
  try {
    if (Array.isArray(flags)) {
      return flags.filter(Boolean).map((flag) => String(flag).trim()).filter(Boolean);
    }
    if (flags === null || flags === undefined || flags === '' || flags === 0 || flags === '0') {
      return [];
    }
    return String(flags)
      .split(',')
      .map((flag) => String(flag || '').trim())
      .filter(Boolean);
  } catch (e) {
    return [];
  }
}

function classifyAnomalyFlags(flags) {
  const normalized = normalizeAnomalyFlags(flags);
  if (!normalized.length) {
    return { severity: null, tip: '' };
  }

  const nonBenign = normalized.filter((flag) => !DQ_BENIGN_ANOMALY_FLAGS.has(flag));
  if (!nonBenign.length) {
    return { severity: null, tip: '' };
  }

  const seriousPattern = /(error|invalid|mismatch|missing|failed|broken|corrupt|duplicate|overflow|underflow|suspicious|stale|reorg|halt|outlier|bad|zero|negative|nan|null|undefined)/i;
  const seriousCount = nonBenign.filter((flag) => seriousPattern.test(flag)).length;

  if (seriousCount > 0) {
    return {
      severity: 'danger',
      tip: `Anomaly: ${nonBenign.join(', ')}`,
    };
  }

  if (nonBenign.length > 1) {
    return {
      severity: 'warning',
      tip: `Potential anomaly: ${nonBenign.join(', ')}`,
    };
  }

  return {
    severity: 'warning',
    tip: `Potential anomaly: ${nonBenign.join(', ')}`,
  };
}

function getDataQualityBadge(token) {
  if (!token) return null;
  const confidence = token.priceConfidence;
  const fragility = token.fragilityScore;
  const anomaly = token.anomalyFlags;

  const anomalyBadge = classifyAnomalyFlags(anomaly);
  const lowConf = (confidence !== null && confidence !== undefined && confidence !== '')
    && (typeof confidence === 'number' ? confidence < DQ_CONFIDENCE_WARN
      : (confidence === 'low' || confidence === 'fallback'));
  const highFrag = (fragility !== null && fragility !== undefined && fragility !== '')
    && Number(fragility) > DQ_FRAGILITY_WARN * 100; // fragilityScore is 0-100

  if (anomalyBadge.severity === 'danger') {
    return { color: '#f44336', tip: anomalyBadge.tip };
  }
  if (highFrag) {
    return { color: '#ff9800', tip: `Fragile price source (score: ${fragility})` };
  }
  if (anomalyBadge.severity === 'warning') {
    return { color: '#ff9800', tip: anomalyBadge.tip };
  }
  if (lowConf) {
    return { color: '#ffd54f', tip: `Low price confidence (${confidence})` };
  }
  return null;
}

const handleLinkClick = (graphData, graphRef, id) => {
  const link = graphData.links.find((n) => n.tx === id);
  if (link) {
    const node = graphData.nodes.find((n) => n.id === link.target.id);
    if (node) {
      const distance = 40;
      const distRatio = 1 + distance/Math.hypot(node.x, node.y, node.z);
  
      graphRef.current.cameraPosition(
        { x: node.x * distRatio, y: node.y * distRatio, z: node.z * distRatio }, // new position
        node, // lookAt ({ x, y, z })
        3000  // ms transition duration
      );
    }
  }
};


export const NodeList = React.forwardRef((props, ref) => {
    const {
      dataSource,
      graphData = { nodes: [], links: [] },
      graphRef,
      linkCount,
      getTransactions,
      tokenList = [],
      handleNodeClick,
      activeNodeId: activeNodeIdProp = null,
      stopCameraSpin,
      viewGraph,
      tokenType,
      rangeSelected,
      organicLayout,
      tokenView,
      showNames,
      pcapShowMac,
      showAddressNodes,
      selectedPriceChangeInterval,
      selectedPriceChangeIntervals,
      selectedPriceTrendInterval,
      onSelectPriceChangeInterval,
      serverPagingEnabled,
      serverSortBy,
      serverSortDir,
      onServerSortChange,
      onLoadMore,
      priceListBusy,
      priceHiddenColumns = [],
      showChainColumn = false,
      hiddenNodeIds,
      onToggleNodeHidden,
      onVisibleCountChange,
      isMobile,
      isPortrait,
      showPriceColumns,
      useColorDots,
      filterAddresses,
      storyAddresses,
      storyOverlayMap,
      showTokenViewToggle,
      includeChainOption,
      onTokenViewChange,
      onOpenCategories,
      categoriesCount,
      listVariant,
      uploadRows,
      selectedUploadIds,
      onToggleUploadSelected,
      onToggleAllUploadsSelected,
      onSparklineClick,
    } = props;
    const classes = useStyles();
    const graphDataRef = useRef(graphData);
    const dataSourceRef = useRef(dataSource);
    const nodeRefs = useRef({});
    const headerSortToggleRef = useRef({});
    const nodeListRef = useRef();
    const failedIconUrlsRef = useRef(new Set());
    const isGraphOrLinkView = useMemo(() => {
      try {
        const pathname = typeof window !== 'undefined' && window.location ? String(window.location.pathname || '') : '';
        return (
          viewGraph ||
          tokenView === 'graphs' ||
          tokenView === 'links' ||
          /^\/(graphs|links)(\/|$)/.test(pathname)
        );
      } catch (e) {
        return viewGraph || tokenView === 'graphs' || tokenView === 'links';
      }
    }, [viewGraph, tokenView]);
    const isGraphsView = useMemo(() => viewGraph && tokenView === 'graphs', [viewGraph, tokenView]);
    const [hoveredRowId, setHoveredRowId] = useState(null);
    const [showPriceDetails, setShowPriceDetails] = useState(false);
    const [showFragilityScoreDetails, setShowFragilityScoreDetails] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [prevNodeId, setPrevNodeId] = useState(null);
    const [activeNodeIdLocal, setActiveNodeId] = useState(null);
    // Prefer the prop (driven by graph clicks) over local state (driven by
    // list-internal navigation).  If no prop is supplied, fall back to local.
    const activeNodeId = activeNodeIdProp != null ? activeNodeIdProp : activeNodeIdLocal;

    const isUploadsVariant = String(listVariant || '').toLowerCase() === 'uploads';

    const uploadSelectionSet = useMemo(() => {
      try {
        if (selectedUploadIds && typeof selectedUploadIds.has === 'function') return selectedUploadIds;
        if (Array.isArray(selectedUploadIds)) return new Set(selectedUploadIds.map((v) => String(v || '').trim()).filter(Boolean));
        return new Set();
      } catch (e) {
        return new Set();
      }
    }, [selectedUploadIds]);

    const formatBytesUploads = (bytes) => {
      const n = Number(bytes);
      if (!Number.isFinite(n) || n <= 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
      const i = Math.min(sizes.length - 1, Math.floor(Math.log(n) / Math.log(k)));
      return `${parseFloat((n / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
    };

    const uploadsData = useMemo(() => {
      const rowsIn = Array.isArray(uploadRows) ? uploadRows : [];
      return rowsIn.map((u) => {
        const uploadId = u && u.upload_id != null ? String(u.upload_id).trim() : '';
        return {
          ...u,
          id: uploadId,
        };
      });
    }, [uploadRows]);

    const storyAddrSet = useMemo(() => {
        try {
            if (!storyAddresses || !Array.isArray(storyAddresses) || !storyAddresses.length) return null;
            return new Set(storyAddresses.map((a) => String(a || '').toLowerCase()).filter(Boolean));
        } catch (e) {
            return null;
        }
    }, [storyAddresses]);

    const filterAddrSet = useMemo(() => {
        try {
            if (!filterAddresses || !Array.isArray(filterAddresses) || !filterAddresses.length) return null;
            return new Set(filterAddresses.map((a) => String(a || '').toLowerCase()).filter(Boolean));
        } catch (e) {
            return null;
        }
    }, [filterAddresses]);

    const { watchlistOnly, watchedKeys, toggleWatch, isWatched, chainFromDataSource, normalizeAddress } = useWatchlist();

    const showMembersOnlyDialog = (e) => {
        try {
            if (e && typeof e.preventDefault === 'function') e.preventDefault();
            if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
        } catch (err) {
        }
        try {
            if (typeof window !== 'undefined' && typeof window.globalShowMembersOnly === 'function') {
                window.globalShowMembersOnly(true);
                return;
            }
            if (typeof document !== 'undefined') {
                document.dispatchEvent(new Event('force-login-dialog-render'));
            }
        } catch (err) {
        }
    };

    const handleGraphRowActivation = (rowId, e) => {
      if (!isGraphOrLinkView) return;
      try {
        if (e && typeof e.button === 'number' && e.button !== 0) return;
        const target = e && e.target instanceof Element ? e.target : null;
        if (target && target.closest && (target.closest('button') || target.closest('[data-watch-toggle="true"]'))) return;
      } catch (err) {
      }
      const address = String(rowId || '').trim();
      if (address && address.startsWith('0x')) {
        window.location.href = `/token/${address}`;
      }
    };

    // Create a filtered copy of tokenList based on the current mode.
    // - In transactions mode, show all token-like nodes that appear in the
    //   graph, even if they were not present in the original tokenList. This
    //   ensures deep-linked tokens and Follow Transactions counterparts (like
    //   Ocean Protocol) appear in the list whenever they exist in graphData.
    // - In other modes, preserve the previous behavior of only filtering
    //   when viewGraph is true.
    const tokenListCopy = useMemo(() => {
        if (isUploadsVariant) return [];
        const graphAddresses = new Set(graphData.nodes.map(node => node.id));
        const graphAddressesLower = new Set(graphData.nodes.map(node => String(node.id || '').toLowerCase()));

        if ((tokenView === 'links' || tokenView === 'graphs') && viewGraph) {
            const rows = [];
            const seen = new Set();

            const dedupeByName = (items) => {
                const out = [];
                const nameToIndex = new Map();

                (items || []).forEach((row) => {
                    if (!row) return;
                    const labelRaw = (row.label || row.name || '').trim();
                    const labelLower = labelRaw.toLowerCase();
                    const isAddressLabel = labelLower.startsWith('0x');

                    const hasIcon = !!(row.icon && String(row.icon).trim().length);

                    // Only dedupe by label for non-address-looking labels.
                    if (!labelRaw || isAddressLabel) {
                        out.push(row);
                        return;
                    }

                    if (!nameToIndex.has(labelLower)) {
                        nameToIndex.set(labelLower, out.length);
                        out.push(row);
                        return;
                    }

                    // Prefer the row that has an icon.
                    const idx = nameToIndex.get(labelLower);
                    const existing = out[idx];
                    const existingHasIcon = !!(existing && existing.icon && String(existing.icon).trim().length);
                    if (!existingHasIcon && hasIcon) {
                        out[idx] = row;
                    }
                });

                return out;
            };

            const hasTokenList = Array.isArray(tokenList) && tokenList.length > 0;

            if (!hasTokenList) {
                // Fallback: tokenList is not populated in /links mode in some
                // flows (dev builds). Derive the list from graphData.nodes so
                // the UI isn't empty.
                graphData.nodes.forEach(node => {
                    const id = node && node.id;
                    if (!id || seen.has(id)) return;
                    if (filterAddrSet && !filterAddrSet.has(String(id).toLowerCase())) return;

                    const looksLikeToken =
                      node.nodeType === 'token' ||
                      !!node.icon ||
                      (typeof node.label === 'string' && !node.label.startsWith('0x'));

                    if (!looksLikeToken && !showAddressNodes && !filterAddrSet) return;

                    seen.add(id);
                    rows.push({
                        id,
                        label: node.label || node.name || id,
                        symbol: node.symbol || node.token_symbol || '',
                        icon: node.icon || null,
                        likes: node.likes ?? 0,
                        chain: (node.chain || '').toUpperCase(),
                    });
                });

                return dedupeByName(rows);
            }

            (tokenList || []).forEach(token => {
                if (!token || !token.id) return;
                const tokenId = String(token.id);
                const tokenIdLower = tokenId.toLowerCase();
                if (!graphAddressesLower.has(tokenIdLower)) return;
                if (filterAddrSet && !filterAddrSet.has(tokenIdLower)) return;
                if (seen.has(tokenIdLower)) return;
                seen.add(tokenIdLower);
                rows.push(token);
            });

            // Merge in any graph nodes that are not already present in tokenList.
            // This keeps /graphs aligned with what is actually shown in the graph,
            // even when the backing token cache is incomplete or stale.
            graphData.nodes.forEach(node => {
                const id = node && node.id;
                if (!id) return;
                const idLower = String(id).toLowerCase();
                if (seen.has(idLower)) return;
                if (filterAddrSet && !filterAddrSet.has(idLower)) return;

                const looksLikeToken =
                  node.nodeType === 'token' ||
                  !!node.icon ||
                  !!node.symbol ||
                  !!node.name ||
                  (typeof node.label === 'string' && !node.label.startsWith('0x'));

                if (!looksLikeToken && !showAddressNodes && !filterAddrSet) return;

                seen.add(idLower);
                rows.push({
                    id,
                    label: node.label || node.name || id,
                    symbol: node.symbol || node.token_symbol || '',
                    icon: node.icon || null,
                    likes: node.likes ?? 0,
                    chain: (node.chain || '').toUpperCase(),
                });
            });

            // In Stories mode, also include graph nodes not in tokenList
            // (wallets, non-named addresses) that are story addresses.
            if (filterAddrSet) {
                graphData.nodes.forEach(node => {
                    const id = node && node.id;
                    const idLower = String(id || '').toLowerCase();
                    if (!id || seen.has(idLower)) return;
                    if (!filterAddrSet.has(idLower)) return;
                    seen.add(idLower);
                    rows.push({
                        id,
                        label: node.label || node.name || id,
                        symbol: node.symbol || node.token_symbol || '',
                        icon: node.icon || null,
                        likes: node.likes ?? 0,
                        chain: (node.chain || '').toUpperCase(),
                    });
                });
            }

            // Optionally include non-token address nodes that exist in the
            // graph but aren't in tokenList.
            if (showAddressNodes) {
                graphData.nodes.forEach(node => {
                    const id = node && node.id;
                    const idLower = String(id || '').toLowerCase();
                    if (!id || seen.has(idLower)) return;

                    const label = typeof node.label === 'string' ? node.label : '';
                    const isAddressLike = label.startsWith('0x') || String(id).startsWith('0x');
                    if (!isAddressLike) return;

                    seen.add(idLower);
                    rows.push({
                        id,
                        label: node.label || node.name || id,
                        symbol: node.symbol || node.token_symbol || '',
                        icon: node.icon || null,
                        likes: node.likes ?? 0,
                        chain: (node.chain || '').toUpperCase(),
                    });
                });
            }

            return dedupeByName(rows);
        }

        // In the Stories inline forensics view, the caller (StoriesLinksPane)
        // has already ordered tokenList so the primary token appears first,
        // followed by named/icon rows and then plain addresses. Preserve that
        // ordering exactly; do not apply any additional sorting here.
        if (tokenView === 'stories') {
          return tokenList;
        }

        // For price-based views, filter to only tokens that appear in the graph,
        // using case-insensitive matching so checksummed vs lowercase addresses match.
        if (tokenView === 'price' || tokenView === 'volatility' || tokenView === 'marketcap') {
          if (!viewGraph) return tokenList;
          return tokenList.filter(token => graphAddressesLower.has(String(token.id || '').toLowerCase()));
        }

        if (!viewGraph) return tokenList;

        return tokenList.filter(token => graphAddresses.has(token.id));
    }, [viewGraph, tokenView, tokenList, graphData, linkCount, showAddressNodes, filterAddrSet]);

    // If every non-empty, non-address label in the current list is the same
    // (e.g. every row is "Ethereum"), then show the address instead of
    // repeating that shared name for all rows.
    const allNamesIdentical = useMemo(() => {
      if (!tokenListCopy || !tokenListCopy.length) return false;

      const names = tokenListCopy
        .map(t => (t.label || '').trim())
        .filter(l => l && !l.startsWith('0x'));

      if (names.length < 2) return false;
      const first = names[0];
      return names.every(l => l === first);
    }, [tokenListCopy]);

    // Log when a prop changes
    /*
    useEffect(() => {
      console.log('graphData changed');
    }, [graphData]);
  
    useEffect(() => {
      console.log('graphRef changed');
    }, [graphRef]);
  
    useEffect(() => {
      console.log('dataSource changed');
    }, [dataSource]);
  
    useEffect(() => {
      console.log('linkCount changed');
    }, [linkCount]);
  
    useEffect(() => {
      console.log('getTransactions changed');
    }, [getTransactions]);
  
    useEffect(() => {
      console.log('stopCameraSpin changed');
    }, [stopCameraSpin]);
  
    useEffect(() => {
      console.log('handleNodeClick changed');
    }, [handleNodeClick]);
  
    useEffect(() => {
      console.log('isMobile changed');
    }, [isMobile]);
  
    useEffect(() => {
      console.log('isPortrait changed');
    }, [isPortrait]);
  
    useEffect(() => {
      console.log('tokenType changed');
    }, [tokenType]);
  
    useEffect(() => {
      console.log('tokenList changed');
    }, [tokenList]);
  */


    useEffect(() => {
      graphDataRef.current = graphData;
      dataSourceRef.current = dataSource;
    }, [graphData, dataSource]);

    useEffect(() => {
      const preventDefaultKeyDown = (event) => {
          if (event.key === 'ArrowDown') {
              event.preventDefault();
          }
      };
      document.addEventListener('keydown', preventDefaultKeyDown);
      return () => document.removeEventListener('keydown', preventDefaultKeyDown);
   }, []);

    useEffect(() => {
      const nodeListElement = nodeListRef.current;
      
      // Modified to keep banner visible
      // Instead of directly focusing which can cause layout shifts,
      // we'll set a custom attribute that doesn't affect layout
      const handleMouseEnter = () => {
          // Set a custom attribute instead of changing focus
          nodeListElement.setAttribute('data-hover', 'true');
          // Still allow keyboard navigation to work
          if (document.activeElement !== nodeListElement) {
              nodeListElement.focus({ preventScroll: true });
          }
      };
      
      const handleMouseLeave = () => {
          nodeListElement.removeAttribute('data-hover');
          // Only blur if we're not actively using keyboard navigation
          if (!document.activeElement || !document.activeElement.classList.contains('node-row')) {
              nodeListElement.blur();
          }
      };
      
      nodeListElement.addEventListener('mouseenter', handleMouseEnter);
      nodeListElement.addEventListener('mouseleave', handleMouseLeave);
      
      return () => {
          nodeListElement.removeEventListener('mouseenter', handleMouseEnter);
          nodeListElement.removeEventListener('mouseleave', handleMouseLeave);
      };
    }, []);
 
    useEffect(() => {
      const nodeListElement = nodeListRef.current;
  
      const handleFocus = () => {
          nodeListElement.focus();
      };
  
      nodeListElement.addEventListener('focus', handleFocus);
  
      return () => {
          nodeListElement.removeEventListener('focus', handleFocus);
      };
    }, []);

    useEffect(() => {
      if (isUploadsVariant) return;
        //console.log("Effect running", { activeNodeId, rowsLength: rows.length });

        const handleKeyDown = (event) => {
            if ((event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
                // Check if the nodeListContainer is currently focused
                if (document.activeElement === nodeListRef.current || tokenType === "NFT") {
                    const currentIndex = rows.findIndex(row => row.original.id === activeNodeId);
                    const newIndex = (event.key === 'ArrowDown') ? currentIndex + 1 : currentIndex - 1;
    
                    // Ensure newIndex is within bounds
                    if (newIndex >= 0 && newIndex < rows.length) {
                        setCurrentIndex(newIndex);
                        const newActiveNodeId = rows[newIndex].original.id;
                        
                        // Update the active node
                        handleNodeClick(dataSourceRef.current, graphDataRef.current, graphRef, newActiveNodeId, false, tokenType);
                        
                        // Scroll to the new node if it's not visible
                        if (!isRowVisible(newIndex)) {
                            scrollToActiveNode(newActiveNodeId);
                        }
                    }
                }
            }
        };
      
        document.addEventListener('keydown', handleKeyDown);
 
        return () => {
          document.removeEventListener('keydown', handleKeyDown);
        };
    }, [activeNodeId, handleNodeClick, dataSourceRef, graphDataRef, graphRef, setCurrentIndex, isUploadsVariant ]); 

    const debounce = (func, delay) => {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), delay);
      };
    };
    
 
    const scrollToActiveNodeLocal = (nodeId) => {
      // Try exact match first, then case-insensitive fallback (graph node IDs
      // and tokenList IDs can differ in checksum casing).
      let element = nodeRefs.current[nodeId];
      if (!element) {
        const lower = String(nodeId || '').toLowerCase();
        const entry = Object.entries(nodeRefs.current).find(([k]) => String(k).toLowerCase() === lower);
        element = entry ? entry[1] : null;
      }

      if (!element) {
        return;
      }

      // Remove highlight from previous node
      if (prevNodeId) {
        const prevElement = nodeRefs.current[prevNodeId];
        if (prevElement) {
          prevElement.classList.remove('highlight');
        }
      }
      setPrevNodeId(nodeId);

      // Find the closest scrollable container
      let container = element;
      while (container && !isScrollable(container)) {
        container = container.parentElement;
      }
 
      if (container) {
        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        // Check if element is already fully visible
        const isVisible = elementRect.top >= containerRect.top && 
                         elementRect.bottom <= containerRect.bottom;
 
        if (!isVisible) {
          // Calculate element's position relative to container
          const elementRelativeTop = elementRect.top - containerRect.top;
          
          // Calculate the ideal scroll position to center the element
          const targetScroll = container.scrollTop + elementRelativeTop - (containerRect.height - elementRect.height) / 2;
          
          // Ensure scroll position is within bounds
          const maxScroll = container.scrollHeight - container.clientHeight;
          const boundedScroll = Math.max(0, Math.min(targetScroll, maxScroll));
          
          container.scrollTo({
            top: boundedScroll,
            behavior: 'smooth'
          });
        }
      }

      // Always highlight the element, whether we scrolled or not
      element.classList.add('highlight');
    };

    // Helper function to check if an element is scrollable
    const isScrollable = (element) => {
      const style = window.getComputedStyle(element);
      const overflowY = style.getPropertyValue('overflow-y');
      return overflowY === 'auto' || overflowY === 'scroll';
    };

    const scrollToActiveNode = (nodeId) => {  
      console.log('scrollToActiveNode called with nodeId:', nodeId);
      // If the same node is selected repeatedly (e.g. pressing Enter on the
      // same search result), React may not re-render because state doesn't
      // change. Force a re-highlight by clearing then re-setting.
      if (activeNodeId === nodeId) {
        setActiveNodeId(null);
        scrollToActiveNodeLocal(nodeId);
        requestAnimationFrame(() => {
          setActiveNodeId(nodeId);
          scrollToActiveNodeLocal(nodeId);
        });
        return;
      }

      setActiveNodeId(nodeId);
      scrollToActiveNodeLocal(nodeId);
    }

    const isInViewport = (element) => {
      const rect = element.getBoundingClientRect();
      const container = element.closest('.MuiTableContainer-root');
      if (!container) return false;
      
      const containerRect = container.getBoundingClientRect();
      return rect.top >= containerRect.top && rect.bottom <= containerRect.bottom;
    };

    const isRowVisible = (rowIndex) => {
        const rowElement = nodeRefs.current[rows[rowIndex].original.id];
        if (!rowElement || !nodeListRef.current) return false;
    
        const listRect = nodeListRef.current.getBoundingClientRect();
        const rowRect = rowElement.getBoundingClientRect();
    
        // Check if the row is within the nodeListContainer's visible bounds
        return rowRect.top >= listRect.top && rowRect.bottom <= listRect.bottom;
    };
  
    const TokenColumns = React.useMemo(
      () => {
        try {
          console.debug('[NodeList] rebuilding TokenColumns', {
            tokenView,
            selectedPriceTrendInterval,
            rangeSelected,
            viewGraph,
            showPriceColumns,
            tokenType,
          });
        } catch (e) {
        }
        const columns = [
          {
            id: 'watch',
            Header: () => (
              <div style={{ textAlign: 'center', width: 22 }}>
                <span className={classes.listTextStyle}>
                    <FaStar style={{ color: '#ffd700' }} />
                </span>
              </div>
            ),
            accessor: () => '',
            disableSortBy: true,
            Cell: ({ row }) => {
              const chain = normalizeWatchChain(row.original.chain || chainFromDataSource(dataSource) || 'ETH');
              const addr = normalizeAddress(row.original.id);
              const disabled = !addr;
              const watched = !disabled && watchedKeys && watchedKeys.has(`${chain}:${addr}`);
              const title = watched ? 'Remove from watchlist' : 'Add to watchlist';

              return (
                <div style={{ textAlign: 'center', width: 22 }}>
                  <Tooltip title={title}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={disabled}
                        type="button"
                        data-watch-toggle="true"
                        onClick={(e) => {
                          try {
                            e.preventDefault();
                            e.stopPropagation();
                          } catch (err) {
                          }
                          toggleWatch({
                            chain,
                            address: addr,
                            label: row.original.symbol || row.original.label || '',
                            type: row.original.type || '',
                          }).catch(() => {
                            showMembersOnlyDialog(e);
                          });
                        }}
                        style={{ padding: 2, pointerEvents: 'auto' }}
                      >
                        <FaStar style={{ color: watched ? '#ffd700' : '#666' }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </div>
              );
            },
          },
          {
            id: 'icon',
            Header: () => (
              <div style={{ textAlign: 'center', marginLeft: 0, width: 22, minWidth: 22, maxWidth: 22 }}>
                <span className={classes.listTextStyle}>&#x1F60A;</span>
              </div>
            ),
            accessor: (row) => {
              try {
                if (!hiddenNodeIds || typeof hiddenNodeIds.has !== 'function') return 0;
                const id = row && row.id != null ? String(row.id) : '';
                if (!id) return 0;
                return hiddenNodeIds.has(id) ? 1 : 0;
              } catch (e) {
                return 0;
              }
            },
            Cell: ({ row }) => {
              const rawIcon = (() => {
                try {
                  return row && row.original && row.original.icon != null ? String(row.original.icon).trim() : '';
                } catch (e) {
                  return '';
                }
              })();
              const normalizedIcon = (() => {
                const v = (rawIcon || '').trim();
                if (!v) return '';
                const lower = v.toLowerCase();
                if (lower === 'null' || lower === 'undefined' || lower === '""') return '';
                return v;
              })();
              const knownFailed = !!normalizedIcon && failedIconUrlsRef.current.has(normalizedIcon);
              const iconSrc = normalizedIcon && !knownFailed ? normalizedIcon : genericTokenIcon;
              const hasCustomIcon = !!normalizedIcon;
              const isHidden = (() => {
                try {
                  if (!hiddenNodeIds || typeof hiddenNodeIds.has !== 'function') return false;
                  const id = row && row.original && row.original.id != null ? String(row.original.id) : '';
                  if (!id) return false;
                  return hiddenNodeIds.has(id);
                } catch (e) {
                  return false;
                }
              })();

              const toggleHidden = (e) => {
                if (!onToggleNodeHidden) return false;
                try {
                  e.preventDefault();
                  e.stopPropagation();
                } catch (err) {
                }
                try {
                  const id = row && row.original && row.original.id != null ? String(row.original.id) : '';
                  if (id && typeof onToggleNodeHidden === 'function') onToggleNodeHidden(id, graphData);
                } catch (err) {
                }
                return true;
              };

              const storyBorderColor = (() => {
                try {
                  const id = row && row.original && row.original.id != null ? String(row.original.id).toLowerCase() : '';
                  if (!id) return null;
                  // storyOverlayMap (with type colors) takes priority over plain storyAddrSet
                  if (storyOverlayMap && typeof storyOverlayMap.get === 'function') {
                    const arr = storyOverlayMap.get(id);
                    if (arr && arr.length) return storyTypeColorHex(arr[0] && arr[0].story_type);
                  }
                  if (storyAddrSet && storyAddrSet.has(id)) return '#4d79ff';
                  return null;
                } catch (e) {
                  return null;
                }
              })();
              const hasStory = !!storyBorderColor;

              return (
                <div
                  className={classes.iconColumn}
                  style={{ textAlign: 'center', marginLeft: 0, width: 22, minWidth: 22, maxWidth: 22 }}
                >
                  <img
                    src={iconSrc}
                    alt=""
                    className={classes.iconColumn}
                    style={{
                      textAlign: 'center',
                      marginLeft: '0px',
                      opacity: isHidden ? 0.25 : 1,
                      boxSizing: 'border-box',
                      borderRadius: 2,
                      display: 'block',
                      border: storyBorderColor ? `2px solid ${storyBorderColor}` : '2px solid transparent',
                      boxShadow: storyBorderColor ? `0 0 4px ${storyBorderColor}` : 'none',
                    }}
                    onClick={(e) => {
                      const handled = toggleHidden(e);
                      if (!handled) handleNodeLinkClick(row.original.id, 'address', e);
                    }}
                    onMouseDown={(e) => {
                      // In graphs/links view, don't block events for navigation
                      if (isGraphOrLinkView) return;
                      try { e.preventDefault(); e.stopPropagation(); } catch (err) {}
                    }}
                    onTouchStart={(e) => {
                      // In graphs/links view, don't block events for navigation
                      if (isGraphOrLinkView) return;
                      try { e.preventDefault(); e.stopPropagation(); } catch (err) {}
                    }}
                    loading="lazy"
                    onError={(e) => {
                      try {
                        const failed = normalizedIcon || rawIcon;
                        if (failed) {
                          failedIconUrlsRef.current.add(failed);
                        }
                        if (e && e.currentTarget) {
                          // Prevent loops, then force generic icon.
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = genericTokenIcon;
                          e.currentTarget.style.display = 'block';
                        }
                      } catch (err) {}
                    }}
                  />
                  {!hasCustomIcon && useColorDots && row.original.color && (
                    <span
                      style={{
                        display: 'inline-block',
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        backgroundColor: row.original.color,
                        opacity: isHidden ? 0.25 : 1,
                        cursor: onToggleNodeHidden ? 'pointer' : 'default',
                      }}
                      onClick={(e) => { toggleHidden(e); }}
                      onMouseDown={(e) => {
                        // In graphs/links view, don't block events for navigation
                        if (isGraphOrLinkView) return;
                        try { e.preventDefault(); e.stopPropagation(); } catch (err) {}
                      }}
                      onTouchStart={(e) => {
                        // In graphs/links view, don't block events for navigation
                        if (isGraphOrLinkView) return;
                        try { e.preventDefault(); e.stopPropagation(); } catch (err) {}
                      }}
                    />
                  )}
                </div>
              );
            },
          },
          {
            Header: () => (
              <div style={{ textAlign: 'left' }}>
                <span className={classes.listTextStyle}>Name</span>
              </div>
            ),
            accessor: 'label',
            width: 120,
            Cell: ({ row }) => {
              if (tokenView === 'price' && isMobile && isPortrait) {
                return (
                  <div style={{ maxWidth: '100px', width: '100px' }}>
                    <button
                      onClick={(e) => handleNodeLinkClick(row.original.id, 'address', e)}
                      className={classes.listTextStyle}
                    >
                      {row.original.symbol || ''}
                    </button>
                  </div>
                );
              }

              if (showNames === false) {
                return (
                  <div
                    style={
                      isMobile && isPortrait
                        ? {
                            maxWidth: viewGraph ? '240px' : '100px',
                            width: viewGraph ? '240px' : '100px',
                          }
                        : !viewGraph
                        ? { maxWidth: '350px', width: '350px' }
                        : { maxWidth: '150px', width: '150px' }
                    }
                  >
                    <button
                      onClick={(e) => handleNodeLinkClick(row.original.id, 'address', e)}
                      className={classes.listTextStyle}
                    >
                      {row.original.id || ''}
                    </button>
                  </div>
                );
              }

              const rawLabel = row.original.label || '';
              const isExactlyDotNft = rawLabel === '.nft';
              let displayLabel = rawLabel;

              if (isExactlyDotNft) {
                displayLabel = formatShortAddress(row.original.id);
              } else if (rawLabel.toLowerCase().endsWith('.nft')) {
                // strip trailing .nft
                displayLabel = rawLabel.slice(0, -4);
              } else if (allNamesIdentical && rawLabel && !rawLabel.startsWith('0x')) {
                // When every name is the same token (e.g. "Ethereum"), that
                // label provides no per-row distinction, so show the address
                // instead to surface what actually varies between rows.
                displayLabel = formatShortAddress(row.original.id);
              }

              return (
                <div
                  style={
                    isMobile && isPortrait
                      ? {
                          maxWidth: viewGraph ? '180px' : '100px',
                          width: viewGraph ? '180px' : '100px',
                        }
                      : !viewGraph
                      ? { maxWidth: '220px', width: '220px' }
                      : { maxWidth: '120px', width: '120px' }
                  }
                >
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      try {
                        e.preventDefault();
                        e.stopPropagation();
                      } catch (err) {
                      }
                      handleNodeLinkClick(row.original.id, 'address', e);
                    }}
                    className={classes.listTextStyle}
                  >
                    {displayLabel}
                  </button>

                  {row.original.symbol && !allNamesIdentical && (
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        try {
                          e.preventDefault();
                          e.stopPropagation();
                        } catch (err) {
                        }
                        handleNodeLinkClick(row.original.id, 'address', e);
                      }}
                      className={classes.listTextStyle}
                      style={{
                        maxWidth: '80px',
                        marginLeft: '6px',
                        color: '#9aa4b2',
                      }}
                    >
                      {row.original.symbol}
                    </button>
                  )}

                  {tokenView === 'price' && (() => {
                    const badge = getDataQualityBadge(row.original);
                    if (!badge) return null;
                    return (
                      <Tooltip title={badge.tip}>
                        <span style={{
                          display: 'inline-block',
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: badge.color,
                          marginLeft: 5,
                          verticalAlign: 'middle',
                          flexShrink: 0,
                          cursor: 'default',
                        }} />
                      </Tooltip>
                    );
                  })()}
                </div>
              );
            },
          },
          ...(tokenView === 'chain' ? [{
            Header: () => (
              <div style={{ textAlign: 'left' }}>
                <span className={classes.listTextStyle}>Address</span>
              </div>
            ),
            accessor: 'address',
            width: 120,
            Cell: ({ row }) => (
              <div style={{ textAlign: 'left', maxWidth: '120px', width: '120px' }}>
                <button onClick={(e) => handleNodeLinkClick(row.original.id, 'address', e)} className={classes.listTextStyle}>
                  {formatShortAddress(row.original.id)}
                </button>
              </div>
            ),
          }] : []),
        ];

        // Show Chain column in Chain mode and on the /price page.
        if (tokenView === 'chain' || showChainColumn) {
          columns.push({
            Header: () => (
              <div style={{ textAlign: 'left' }}>
                <span className={classes.listTextStyle}>Chain</span>
              </div>
            ),
            accessor: 'chain',
            Cell: ({ row }) => {
              const rawChain = (row.original.chain || '').toUpperCase();
              // Match the chain colors used in the graph label spheres so the
              // table and graph are visually consistent.
              let chainColor = '#555555';

              if (rawChain === 'ETH') {
                chainColor = '#3f51b5';
              } else if (rawChain === 'ARB') {
                chainColor = '#00bcd4';
              } else if (rawChain === 'OPT') {
                chainColor = '#e53935';
              } else if (rawChain === 'BSC') {
                chainColor = '#fbc02d';
              } else if (rawChain === 'POL') {
                chainColor = '#9c27b0';
              } else if (rawChain === 'AVAX') {
                chainColor = '#ef6c00';
              } else if (rawChain === 'SOL') {
                chainColor = '#9945ff';
              }

              const display = rawChain || '-';

              return (
                <div style={{ maxWidth: '70px', width: '70px' }}>
                  <button
                    onClick={(e) => handleNodeLinkClick(row.original.id, 'address', e)}
                    className={classes.listTextStyle}
                    style={{
                      maxWidth: '70px',
                      padding: '2px 6px',
                      borderRadius: '8px',
                      backgroundColor: 'transparent',
                      color: chainColor,
                      border: 'none',
                    }}
                  >
                    {display}
                  </button>
                </div>
              );
            },
          });
        }

        // Show Likes in most views, but suppress it in MarketCap mode so the
        // MCap column effectively replaces it.
        if (tokenView == 'likes') {
          columns.push({
            Header: () => (
              <div style={{ textAlign: 'right', paddingRight: '10px' }}>
                <span className={classes.listTextStyle}>Likes</span>
              </div>
            ),
            accessor: 'likes',
            Cell: ({ row }) => (
              <div style={{ textAlign: 'right', paddingRight: '10px' }}>
                <button onClick={(e) => handleNodeLinkClick(row.original.id, 'address', e)} className={classes.listTextStyle} >
                  {(row.original.likes) ? row.original.likes : '-'}
                </button>
              </div>
            ),
          });
        }

        if (!viewGraph || tokenView === 'chain') {
          columns.push({
            Header: () => (
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
                <span className={classes.listTextStyle}>Stories</span>
              </div>
            ),
            accessor: 'storyCount',
            Cell: ({ row }) => (
              <div style={{ textAlign: 'right' }}>
                <button
                  onClick={(e) => handleNodeLinkClick(row.original.id, 'address', e)}
                  className={classes.listTextStyle}
                  style={{ color: (row.original.storyCount > 0) ? 'RGB(0, 255, 0)' : '#777' }}
                >
                  {(row.original.storyCount > 0) ? row.original.storyCount : ''}
                </button>
              </div>
            ),
            sortType: (rowA, rowB) => {
              const a = parseFloat(rowA.original.storyCount || 0);
              const b = parseFloat(rowB.original.storyCount || 0);
              return a - b;
            },
          });

          columns.push({
            Header: () => (
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
                <span className={classes.listTextStyle}>Theses</span>
              </div>
            ),
            accessor: 'thesisCount',
            Cell: ({ row }) => (
              <div style={{ textAlign: 'right' }}>
                <button
                  onClick={(e) => handleNodeLinkClick(row.original.id, 'address', e)}
                  className={classes.listTextStyle}
                  style={{ color: (row.original.thesisCount > 0) ? 'RGB(0, 255, 0)' : '#777' }}
                >
                  {(row.original.thesisCount > 0) ? row.original.thesisCount : ''}
                </button>
              </div>
            ),
            sortType: (rowA, rowB) => {
              const a = parseFloat(rowA.original.thesisCount || 0);
              const b = parseFloat(rowB.original.thesisCount || 0);
              return a - b;
            },
          });
        }

        // Price column: only shown in Price mode. Volatility, Transactions,
        // and Chain modes rely on other columns instead.
        if (tokenView === 'price') {
          columns.push({
            Header: () => (
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
                <span className={classes.listTextStyle}>Price</span>
              </div>
            ),
            accessor: 'price',
            Cell: ({ row }) => (
              <div style={{ textAlign: 'right' }}>
                <Tooltip title={formatPriceQualityTooltip(row.original) || ''}>
                  <button 
                    onClick={(e) => handleNodeLinkClick(row.original.id, 'address', e)} 
                    className={classes.listTextStyle} 
                    style={{ color: row.original.priceConfidence === 'low' || row.original.priceConfidence === 'fallback' ? '#ffd54f' : 'RGB(0, 255, 0)', whiteSpace: 'nowrap' }}
                  >
                    {(row.original.price) ? formatPrice(row.original.price) : '-'}
                  </button>
                </Tooltip>
              </div>
            ),
            sortType: (rowA, rowB) => {
              const priceA = parseFloat(rowA.original.price);
              const priceB = parseFloat(rowB.original.price);
              return priceA - priceB;
            },
          });          
        }
        
        
        // Show Market Cap in all non-chain token views. In MarketCap
        // mode we want this even when viewGraph is true so users can see
        // the actual market cap alongside the ranked sphere layout.
        if (tokenView !== 'chain') {
           columns.push({
             Header: () => (
               <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
                 <span className={classes.listTextStyle}>MCap</span>
               </div>
             ),
             accessor: 'marketCap',
             Cell: ({ row }) => (
               <div style={{ textAlign: 'right' }}>
                 <Tooltip title={(row.original.marketCap ? `$${Number(row.original.marketCap).toLocaleString()}\n` : '') + (formatPriceQualityTooltip(row.original) || '')}>
                   <button onClick={(e) => handleNodeLinkClick(row.original.id, 'address', e)} className={classes.listTextStyle} style={{ color: Array.isArray(row.original.anomalyFlags) && row.original.anomalyFlags.includes('market_cap_uses_total_supply') ? 'yellow' : 'RGB(0, 255, 0)' }}>
                     {(row.original.marketCap) ? formatCompact(row.original.marketCap) : '-'}
                   </button>
                 </Tooltip>
               </div>
             ),
             sortType: (rowA, rowB) => {
               const capA = parseFloat(rowA.original.marketCap);
               const capB = parseFloat(rowB.original.marketCap);
               return capA - capB;
             },
           });
        }
        
        if (!viewGraph && !isMobile) {
          columns.push({
            Header: () => (
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
                <span className={classes.listTextStyle}>Total Supply</span>
              </div>
            ),
            accessor: 'totalSupply',
            Cell: ({ row }) => (
              <div style={{ textAlign: 'right' }}>
                <button onClick={(e) => handleNodeLinkClick(row.original.id, 'address', e)} className={classes.listTextStyle} style={{ color: 'RGB(0, 255, 0)' }}>
                  {(row.original.totalSupply || row.original.supply) ? formatCompactTokens(row.original.totalSupply || row.original.supply) : '-'}
                </button>
              </div>
            ),
          });
        }

        if (!viewGraph && !isMobile && tokenView === 'price') {
          columns.push({
            Header: () => (
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
                <span className={classes.listTextStyle}>Circ Supply</span>
              </div>
            ),
            accessor: 'circulatingSupply',
            Cell: ({ row }) => (
              <div style={{ textAlign: 'right' }}>
                <button onClick={(e) => handleNodeLinkClick(row.original.id, 'address', e)} className={classes.listTextStyle} style={{ color: 'RGB(0, 255, 0)' }}>
                  {formatCompactTokens(row.original.circulatingSupply)}
                </button>
              </div>
            ),
            sortType: (rowA, rowB) => {
              const a = parseFloat(rowA.original.circulatingSupply || 0);
              const b = parseFloat(rowB.original.circulatingSupply || 0);
              return a - b;
            },
          });

          columns.push({
            Header: () => (
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
                <span className={classes.listTextStyle}>FDV</span>
              </div>
            ),
            accessor: 'fdvUSD',
            Cell: ({ row }) => (
              <div style={{ textAlign: 'right' }}>
                <Tooltip title={(row.original.fdvUSD ? `$${Number(row.original.fdvUSD).toLocaleString()}\n` : '') + (formatPriceQualityTooltip(row.original) || 'FDV derived from current supply and price')}>
                  <button onClick={(e) => handleNodeLinkClick(row.original.id, 'address', e)} className={classes.listTextStyle} style={{ color: 'RGB(0, 255, 0)' }}>
                    {(row.original.fdvUSD) ? formatCompact(row.original.fdvUSD) : '-'}
                  </button>
                </Tooltip>
              </div>
            ),
            sortType: (rowA, rowB) => {
              const a = parseFloat(rowA.original.fdvUSD || 0);
              const b = parseFloat(rowB.original.fdvUSD || 0);
              return a - b;
            },
          });

          columns.push({
            Header: () => (
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
                <span className={classes.listTextStyle}>Liquidity</span>
              </div>
            ),
            accessor: 'liquidityUSD',
            Cell: ({ row }) => (
              <div style={{ textAlign: 'right' }}>
                <Tooltip title={(row.original.liquidityUSD ? `$${Number(row.original.liquidityUSD).toLocaleString()}\n` : '') + (formatLiquidityTooltip(row.original) || 'Source liquidity data')}>
                  <button
                    onClick={(e) => handleNodeLinkClick(row.original.id, 'address', e)}
                    className={classes.listTextStyle}
                    style={{ color: (row.original.liquidityUSD === null || row.original.liquidityUSD === undefined || Number(row.original.liquidityUSD) <= 0) ? '#777' : 'RGB(0, 255, 0)' }}
                  >
                    {(row.original.liquidityUSD) ? formatCompact(row.original.liquidityUSD) : '-'}
                  </button>
                </Tooltip>
              </div>
            ),
            sortType: (rowA, rowB) => {
              const a = parseFloat(rowA.original.liquidityUSD || 0);
              const b = parseFloat(rowB.original.liquidityUSD || 0);
              return a - b;
            },
          });

          columns.push({
            Header: () => (
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
                <span className={classes.listTextStyle}>Vol 24H</span>
              </div>
            ),
            accessor: 'volume24hUSD',
            Cell: ({ row }) => (
              <div style={{ textAlign: 'right' }}>
                <span className={classes.listTextStyle} style={{ color: (row.original.volume24hUSD && Number(row.original.volume24hUSD) > 0) ? 'RGB(0, 255, 0)' : '#777' }}>
                  {(row.original.volume24hUSD && Number(row.original.volume24hUSD) > 0) ? formatCompact(row.original.volume24hUSD) : '-'}
                </span>
              </div>
            ),
            sortType: (rowA, rowB) => {
              const a = parseFloat(rowA.original.volume24hUSD || 0);
              const b = parseFloat(rowB.original.volume24hUSD || 0);
              return a - b;
            },
          });

          columns.push({
            Header: () => (
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
                <span className={classes.listTextStyle}>Eff. Liq</span>
              </div>
            ),
            accessor: 'effectiveLiquidityUSD',
            Cell: ({ row }) => (
              <div style={{ textAlign: 'right' }}>
                <Tooltip title={(row.original.effectiveLiquidityUSD ? `$${Number(row.original.effectiveLiquidityUSD).toLocaleString()}\n` : '') + (formatLiquidityTooltip(row.original) || 'Effective liquidity estimate')}>
                  <button onClick={(e) => handleNodeLinkClick(row.original.id, 'address', e)} className={classes.listTextStyle} style={{ color: 'RGB(0, 255, 0)' }}>
                    {(row.original.effectiveLiquidityUSD) ? formatCompact(row.original.effectiveLiquidityUSD) : '-'}
                  </button>
                </Tooltip>
              </div>
            ),
            sortType: (rowA, rowB) => {
              const a = parseFloat(rowA.original.effectiveLiquidityUSD || 0);
              const b = parseFloat(rowB.original.effectiveLiquidityUSD || 0);
              return a - b;
            },
          });

          const renderTrendSparkline = (trendPayload, selectedTrend, labelKey) => {
            const trend = trendPayload || {};
            // For volume, EthAmmSwapEvents only keeps ~24h so short intervals are often empty.
            // Fall back through progressively longer ranges until we find one with points.
            const RANGE_FALLBACK_ORDER = ['12H', '24H', '7D', '30D', '6H', '30M'];
            const getSeriesPoints = (key) => {
              const s = trend && typeof trend === 'object' ? trend[key] : null;
              return (s && Array.isArray(s.points) && s.points.length) ? s.points : null;
            };
            let selectedPts = null;
            // Always try the requested interval first.
            selectedPts = getSeriesPoints(selectedTrend);
            // If empty (common for volume on short intervals), walk through fallback order.
            if (!selectedPts) {
              for (const k of RANGE_FALLBACK_ORDER) {
                selectedPts = getSeriesPoints(k);
                if (selectedPts) break;
              }
            }
            // Final fallback: flat points array on the payload itself (legacy shape).
            if (!selectedPts && Array.isArray(trend.points) && trend.points.length) {
              selectedPts = trend.points;
            }
            selectedPts = selectedPts || [];
            const valueKeys = labelKey === 'volume'
              ? ['volume_usd', 'volumeUSD', 'volume']
              : ['price_usd', 'priceUSD', 'price'];
            const values = selectedPts.map((pt) => {
              for (let i = 0; i < valueKeys.length; i += 1) {
                const key = valueKeys[i];
                const v = Number(pt && pt[key]);
                if (Number.isFinite(v)) return v;
              }
              return NaN;
            }).filter((v) => Number.isFinite(v) && v >= 0);
            if (values.length < 2) return <div style={{ width: 60 }}>—</div>;

            const min = Math.min(...values);
            const max = Math.max(...values);
            const range = max - min || 1;
            const scaleValue = (v) => Math.max(0, Math.min(1, (v - min) / range));

            const w = 60;
            const h = 24;
            const pad = 2;
            const xs = values.map((_, i) => pad + (i / (values.length - 1)) * (w - pad * 2));
            const ys = values.map((v) => (h - pad) - scaleValue(v) * (h - pad * 2));
            const d = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(' ');
            // Strict comparisons so a flat series (common for zero-liquidity tokens whose
            // price is only updated via an infrequent external fallback source) renders as
            // neutral gray instead of being miscounted as "up" and painted green.
            const first = values[0];
            const last = values[values.length - 1];
            const strokeColor = last > first ? '#00e676' : last < first ? '#ff5252' : '#9e9e9e';

            return (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <svg width={w} height={h} style={{ display: 'block' }}>
                  <path d={d} fill="none" stroke={strokeColor} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            );
          };

          columns.push({
            id: 'trend_sparkline',
            Header: () => (
              <div style={{ textAlign: 'center' }}>
                <span
                  className={classes.listTextStyle}
                  title="Price Trend"
                  style={{ textAlign: 'right' }}
                >
                  Price Trend
                </span>
              </div>
            ),
            disableSortBy: true,
            Cell: ({ row }) => {
              const selectedTrend = String(selectedPriceTrendInterval || '30D').trim().toUpperCase();
              const hasClick = typeof onSparklineClick === 'function';
              return (
                <div
                  style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: hasClick ? 'pointer' : 'default' }}
                  onClick={hasClick ? (e) => { e.stopPropagation(); onSparklineClick(row.original); } : undefined}
                  title={hasClick ? 'Click to expand chart' : undefined}
                >
                  {renderTrendSparkline(row.original.priceTrend, selectedTrend, 'price')}
                </div>
              );
            },
          });

          columns.push({
            id: 'volume_trend_sparkline',
            Header: () => (
              <div style={{ textAlign: 'center' }}>
                <span
                  className={classes.listTextStyle}
                  title="Volume Trend"
                  style={{ textAlign: 'right' }}
                >
                  Volume Trend
                </span>
              </div>
            ),
            disableSortBy: true,
            Cell: ({ row }) => {
              const selectedTrend = String(selectedPriceTrendInterval || '30D').trim().toUpperCase();
              return (
                <div
                  style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                  title="Volume trend"
                >
                  {renderTrendSparkline(row.original.volumeTrend, selectedTrend, 'volume')}
                </div>
              );
            },
          });

          columns.push({
            Header: () => (
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
                <span className={classes.listTextStyle}>Fragility</span>
              </div>
            ),
            accessor: 'fragilityScore',
            width: 56,
            Cell: ({ row }) => (
              <div style={{ textAlign: 'right', width: '56px', maxWidth: '56px' }}>
                <Tooltip title={formatPriceQualityTooltip(row.original) || 'Fragility risk signal'}>
                  <button onClick={(e) => handleNodeLinkClick(row.original.id, 'address', e)} className={classes.listTextStyle} style={{ color: 'RGB(0, 255, 0)' }}>
                    {(row.original.fragilityScore !== null && row.original.fragilityScore !== undefined) ? row.original.fragilityScore : '-'}
                  </button>
                </Tooltip>
              </div>
            ),
            sortType: (rowA, rowB) => {
              const a = parseFloat(rowA.original.fragilityScore || 0);
              const b = parseFloat(rowB.original.fragilityScore || 0);
              return a - b;
            },
          });

        }

        // In the Stories inline forensics view, show a simple Txs column
        // based on the precomputed links count for each token/address.
        if (tokenView === 'stories') {
          columns.push({
            id: 'type',
            Header: () => (
              <div style={{ textAlign: 'left' }}>
                <span className={classes.listTextStyle}>Type</span>
              </div>
            ),
            accessor: 'type',
            Cell: ({ row }) => (
              <div style={{ maxWidth: '120px', width: '120px' }}>
                <span className={classes.listTextStyle}>{row.original.type || '-'}</span>
              </div>
            ),
          });
          columns.push({
            id: 'links',
            Header: () => (
              <div style={{ textAlign: 'right', paddingRight: 8 }}>
                <span className={classes.listTextStyle}>Links</span>
              </div>
            ),
            accessor: 'links',
            Cell: ({ row }) => (
              <div style={{ textAlign: 'right', paddingRight: 8 }}>
                <span className={classes.listTextStyle}>{row.original.links ?? 0}</span>
              </div>
            ),
          });
        }

        // Commented out Uniswap column for now
        /*
        if (!viewGraph && !isMobile) {
          columns.push({
            Header: () => (
              <div style={{ textAlign: 'right' }}>Uniswap</div>
            ),
            accessor: 'version',
            Cell: ({ row }) => (
              <div style={{ textAlign: 'right' }}>
                <button onClick={(e) => handleNodeLinkClick(row.original.id, 'address', e)} className={classes.listTextStyle} style={{ color: 'RGB(0, 255, 0)' }}>
                  {(row.original.version) ? row.original.version : '-'}
                </button>
              </div>
            ),
          });
        }
        */

        // Add price-change columns for cached intervals in non-organic token views
        const addChangeColumn = (intervalKey, label) => {
          const MAX_ABS_CHANGE = 1000; // clamp to +/- 1,000%
          const NEUTRAL_EPSILON = 0.00005; // treat extremely small deltas as 0

          const clampPercent = (raw) => {
            const value = Number(raw);
            if (!isFinite(value)) return null;
            if (Math.abs(value) > MAX_ABS_CHANGE) {
              return value > 0 ? MAX_ABS_CHANGE : -MAX_ABS_CHANGE;
            }
            return value;
          };

          columns.push({
            id: `change_${intervalKey}`,
            Header: () => (
              <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                <span className={classes.listTextStyle}>{label}</span>
              </div>
            ),
            accessor: row => {
              const changes = row.changes || row.original?.changes;
              const entry = changes && changes[intervalKey];
              if (!entry || entry.hasHistory === false || entry.percent === undefined || entry.percent === null) {
                return null;
              }
              return clampPercent(entry.percent);
            },
            Cell: ({ row }) => {
              const changes = row.original.changes;
              const entry = changes && changes[intervalKey];
              if (!entry || entry.hasHistory === false || entry.percent === undefined || entry.percent === null) {
                return (
                  <div style={{ textAlign: 'right', color: '#666' }}>
                    <span className={classes.listTextStyle}>-</span>
                  </div>
                );
              }

              const clamped = clampPercent(entry.percent);
              if (clamped === null) {
                return (
                  <div style={{ textAlign: 'right', color: '#666' }}>
                    <span className={classes.listTextStyle}>-</span>
                  </div>
                );
              }

              const value = clamped;
              const rawValue = Number(entry.percent);
              const isClamped = isFinite(rawValue) && Math.abs(rawValue) > MAX_ABS_CHANGE;
              const absValue = Math.abs(value);
              const isNeutral = absValue <= NEUTRAL_EPSILON;
              const isUp = !isNeutral && value > 0;
              const isDown = !isNeutral && value < 0;
              const color = isNeutral ? '#d8d8d8' : (isUp ? '#4caf50' : '#f44336');
              const arrow = isNeutral ? '' : (isUp ? '▲' : '▼');
              const decimals = intervalKey === '1H' ? 4 : 2;
              const suffix = isClamped ? '+'
                : '';

              return (
                <div style={{ textAlign: 'right', width: '66px', maxWidth: '66px', paddingRight: '8px' }}>
                  <button
                    className={classes.listTextStyle}
                    style={{ color, cursor: 'default' }}
                  >
                    {isNeutral ? `${absValue.toFixed(decimals)}%${suffix}` : `${arrow} ${absValue.toFixed(decimals)}%${suffix}`}
                  </button>
                </div>
              );
            },
          });
        };

        // Only show price-change columns when not in organic user graph mode.
        // For Volatility mode on tokens, we always show a single column that
        // corresponds to the active time range. However, the cached meta
        // currently uses keys like '30M', '6H', '12H', '24H'. When the UI
        // selects '10M', there is no literal '10M' key in changes[], so we map
        // it to the closest cached interval ('30M') while keeping the header
        // label as '10M'. Chain mode intentionally suppresses these
        // columns.
        if (!organicLayout || tokenView === 'volatility' || tokenView === 'price' || tokenView === 'marketcap') {
          if (tokenView === 'volatility' && rangeSelected) {
            // Map UI ranges to the closest cached key in the price meta.
            const intervalKeyMap = {
              // The price-history cache exposes the short-range change as 1H.
              // The graph toolbar still uses 1M/2M/5M/10M labels, so keep the
              // UI label but read from the real cached key.
              '1M':  '1H',
              '2M':  '1H',
              '5M':  '1H',
              '10M': '1H',
              '30M': '1H',
              '6H': '6H',
              '8H': '6H',
              '12H': '12H',
              '24H': '24H',
            };

            const intervalKey = intervalKeyMap[rangeSelected] || rangeSelected;
            addChangeColumn(intervalKey, rangeSelected);
          } else if (showPriceColumns && tokenView === 'price') {
            if (isMobile && isPortrait) {
              const k = selectedPriceChangeInterval || '24H';
              addChangeColumn(k, `${k} %`);
            } else {
              const intervals = Array.isArray(selectedPriceChangeIntervals) && selectedPriceChangeIntervals.length
                ? selectedPriceChangeIntervals
                : ['6H', '12H', '24H'];
              intervals.forEach((k) => addChangeColumn(k, `${k} %`));
            }
          }
        }

        if (viewGraph && (tokenView === 'links' || tokenView === 'graphs')) {
          // In graph views for Transactions mode, show Volume (Txs) based on node volume sparkline
          columns.push({
            Header: () => (
              <div style={{ textAlign: 'right' }}>Txs</div>
            ),
            accessor: 'links',
            width: 120,
            Cell: ({ row }) => {
              const rowId = String(row.original.id || '').toLowerCase();
              const matchingNode = graphData.nodes.find(node => String(node.id || '').toLowerCase() === rowId);
              const volume = matchingNode ? matchingNode.volume : [];

              return (
                <div style={{ textAlign: 'right', width: "100%" }}>
                  <Sparklines data={volume} width={100} height={14}>
                    <SparklinesLine color="RGB(0, 255, 0)" />
                  </Sparklines>
                </div>
              );
            },
          });
        }

        // Safety override: ensure the Price column is only present in Price
        // mode. If any 'price' accessor slipped in for other modes, remove it.
        const finalColumns =
          tokenView === 'price'
            ? columns
            : columns.filter(col => col.accessor !== 'price');

        return finalColumns;
      },
      [
        viewGraph,
        classes,
        isMobile,
        isPortrait,
        setShowPriceDetails,
        tokenView,
        showNames,
        rangeSelected,
        selectedPriceChangeInterval,
        selectedPriceChangeIntervals,
        selectedPriceTrendInterval,
        organicLayout,
        showPriceColumns,
        graphData,
        watchedKeys,
        toggleWatch,
        chainFromDataSource,
        normalizeAddress,
        hiddenNodeIds,
        onToggleNodeHidden,
        storyOverlayMap,
        storyAddrSet,
      ]
    );

    const UploadColumns = React.useMemo(
      () => [
        {
          id: 'select',
          Header: () => {
            const total = uploadsData.length;
            const selectedCount = uploadSelectionSet ? uploadSelectionSet.size : 0;
            const checked = total > 0 && selectedCount === total;
            const indeterminate = selectedCount > 0 && selectedCount < total;
            return (
              <div style={{ textAlign: 'center', width: 22 }}>
                <Checkbox
                  checked={checked}
                  indeterminate={indeterminate}
                  onChange={(e) => {
                    try {
                      if (typeof onToggleAllUploadsSelected !== 'function') return;
                      onToggleAllUploadsSelected(!!(e && e.target && e.target.checked));
                    } catch (err) {
                    }
                  }}
                  style={{ color: '#fff', padding: 0 }}
                />
              </div>
            );
          },
          accessor: () => '',
          disableSortBy: true,
          Cell: ({ row }) => {
            const uploadId = row && row.original && row.original.upload_id != null ? String(row.original.upload_id).trim() : '';
            const checked = uploadId ? (uploadSelectionSet && uploadSelectionSet.has(uploadId)) : false;
            return (
              <div style={{ textAlign: 'center', width: 22 }}>
                <Checkbox
                  checked={!!checked}
                  onChange={() => {
                    try {
                      if (!uploadId || typeof onToggleUploadSelected !== 'function') return;
                      onToggleUploadSelected(uploadId);
                    } catch (err) {
                    }
                  }}
                  disabled={!uploadId}
                  style={{ color: '#fff', padding: 0 }}
                />
              </div>
            );
          },
        },
        {
          id: 'filename',
          Header: () => (
            <div style={{ textAlign: 'left' }}>
              <span className={classes.listTextStyle}>Filename</span>
            </div>
          ),
          accessor: (row) => (row && row.filename != null ? String(row.filename) : ''),
          Cell: ({ row }) => (
            <div style={{ textAlign: 'left', maxWidth: 380, width: 380 }}>
              <span className={classes.listTextStyle}>{row && row.original && row.original.filename ? String(row.original.filename) : ''}</span>
            </div>
          ),
        },
        {
          id: 'created_at',
          Header: () => (
            <div style={{ textAlign: 'left', width: 170, maxWidth: 170 }}>
              <span className={classes.listTextStyle}>Created</span>
            </div>
          ),
          accessor: (row) => (row && row.created_at != null ? String(row.created_at) : ''),
          Cell: ({ row }) => (
            <div style={{ textAlign: 'left', width: 170, maxWidth: 170 }}>
              <span className={classes.listTextStyle}>{row && row.original && row.original.created_at ? String(row.original.created_at) : ''}</span>
            </div>
          ),
          sortType: (rowA, rowB) => (Number(rowA.values.created_at || 0) - Number(rowB.values.created_at || 0)),
        },
        {
          id: 'status',
          Header: () => (
            <div style={{ textAlign: 'left' }}>
              <span className={classes.listTextStyle}>Status</span>
            </div>
          ),
          accessor: (row) => (row && row.status != null ? String(row.status) : ''),
          Cell: ({ row }) => (
            <div style={{ textAlign: 'left', width: 120, maxWidth: 120 }}>
              <span className={classes.listTextStyle}>{row && row.original && row.original.status ? String(row.original.status) : ''}</span>
            </div>
          ),
        },
        {
          id: 'flow_count',
          Header: () => (
            <div style={{ textAlign: 'right' }}>
              <span className={classes.listTextStyle}>Flows</span>
            </div>
          ),
          accessor: (row) => {
            const n = row && row.flow_count != null ? Number(row.flow_count) : 0;
            return Number.isFinite(n) ? n : 0;
          },
          Cell: ({ row }) => (
            <div style={{ textAlign: 'right', width: 70, maxWidth: 70 }}>
              <span className={classes.listTextStyle}>{row && row.original && row.original.flow_count != null ? String(row.original.flow_count) : '-'}</span>
            </div>
          ),
          sortType: (rowA, rowB) => (Number(rowA.values.flow_count || 0) - Number(rowB.values.flow_count || 0)),
        },
        {
          id: 'traffic_bytes',
          Header: () => (
            <div style={{ textAlign: 'right' }}>
              <span className={classes.listTextStyle}>Bytes</span>
            </div>
          ),
          accessor: (row) => {
            const n = row && row.traffic_bytes != null ? Number(row.traffic_bytes) : 0;
            return Number.isFinite(n) ? n : 0;
          },
          Cell: ({ row }) => (
            <div style={{ textAlign: 'right', width: 90, maxWidth: 90 }}>
              <span className={classes.listTextStyle}>{formatBytesUploads(row && row.original ? row.original.traffic_bytes : 0)}</span>
            </div>
          ),
          sortType: (rowA, rowB) => (Number(rowA.values.traffic_bytes || 0) - Number(rowB.values.traffic_bytes || 0)),
        },
      ],
      [classes, uploadsData, uploadSelectionSet, onToggleUploadSelected, onToggleAllUploadsSelected]
    );

    const NFTColumns = React.useMemo(
      () => [
    
        {
          id: 'watch',
          Header: () => (
            <div style={{ textAlign: 'center', width: 22 }}>
              <span className={classes.listTextStyle}></span>
            </div>
          ),
          accessor: () => '',
          disableSortBy: true,
          Cell: ({ row }) => {
            const chain = normalizeWatchChain(row.original.chain || chainFromDataSource(dataSource) || 'ETH');
            const addr = normalizeAddress(row.original.id);
            const disabled = !addr;
            const watched = !disabled && watchedKeys && watchedKeys.has(`${chain}:${addr}`);
            const title = watched ? 'Remove from watchlist' : 'Add to watchlist';

            return (
              <div style={{ textAlign: 'center', width: 22 }}>
                <Tooltip title={title}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={disabled}
                      type="button"
                      data-watch-toggle="true"
                      onClick={(e) => {
                        try {
                          e.preventDefault();
                          e.stopPropagation();
                        } catch (err) {
                        }
                        toggleWatch({
                          chain,
                          address: addr,
                          label: row.original.label || '',
                          type: row.original.type || 'token',
                        }).catch(() => {
                          showMembersOnlyDialog(e);
                        });
                      }}
                      style={{ padding: 2, pointerEvents: 'auto' }}
                    >
                      <FaStar style={{ color: watched ? '#ffd700' : '#666' }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            );
          },
        },
        {
          id: 'icon', 
          Header: () => (
            <div style={{ textAlign: 'center', marginLeft: 0, width: 22, minWidth: 22, maxWidth: 22 }}>
              <span className={classes.listTextStyle}>&#x1F60A;</span>
            </div>
          ),
          accessor: (row) => {
            try {
              if (!hiddenNodeIds || typeof hiddenNodeIds.has !== 'function') return 0;
              const id = row && row.id != null ? String(row.id) : '';
              if (!id) return 0;
              return hiddenNodeIds.has(id) ? 1 : 0;
            } catch (e) {
              return 0;
            }
          },
          Cell: ({ row }) => {
            const iconSrc = row && row.original && row.original.icon ? String(row.original.icon).trim() : '';
            const knownFailedIcon = !!iconSrc && failedIconUrlsRef.current.has(iconSrc);
            const [imageFailed, setImageFailed] = React.useState(knownFailedIcon);
            const isHidden = (() => {
              try {
                if (!hiddenNodeIds || typeof hiddenNodeIds.has !== 'function') return false;
                const id = row && row.original && row.original.id != null ? String(row.original.id) : '';
                if (!id) return false;
                return hiddenNodeIds.has(id);
              } catch (e) {
                return false;
              }
            })();
            React.useEffect(() => {
              setImageFailed(knownFailedIcon);
            }, [iconSrc, knownFailedIcon]);

            const displayIconSrc = iconSrc && (imageFailed || knownFailedIcon) ? genericTokenIcon : iconSrc;
            const toggleHidden = (e) => {
              if (!onToggleNodeHidden) return false;
              try {
                e.preventDefault();
                e.stopPropagation();
              } catch (err) {
              }
              try {
                const id = row && row.original && row.original.id != null ? String(row.original.id) : '';
                if (id && typeof onToggleNodeHidden === 'function') onToggleNodeHidden(id, graphData);
              } catch (err) {
              }
              return true;
            };

            return (
                <div className={classes.iconColumn} style={{ textAlign: 'center', marginLeft: '4px' }} >
                  {!!displayIconSrc && (
                      <img src={displayIconSrc} alt="" className={classes.iconColumn} style={{ textAlign: 'center', marginLeft: '4px', opacity: isHidden ? 0.25 : 1 }}
                        onClick={(e) => {
                          const handled = toggleHidden(e);
                          if (!handled) handleNodeLinkClick(row.original.id, 'NFT', e);
                        }}
                        onMouseDown={(e) => { try { e.preventDefault(); e.stopPropagation(); } catch (err) {} }}
                        onTouchStart={(e) => { try { e.preventDefault(); e.stopPropagation(); } catch (err) {} }}
                        loading="lazy"
                        onError={(e) => {
                          try {
                            if (iconSrc) failedIconUrlsRef.current.add(iconSrc);
                            if (e && e.currentTarget && e.currentTarget.src !== genericTokenIcon) {
                              e.currentTarget.src = genericTokenIcon;
                              return;
                            }
                          } catch (err) {
                          }
                          setImageFailed(true);
                        }} /> 
                  )}
              </div>
            );
          },
        }, 
        {
          id: 'label', 
          Header: () => (
            <div style={{ textAlign: 'left' }}>
              <span className={classes.listTextStyle}>NFT Collection Name</span>
            </div>
          ),
          accessor: row => String(row && row.label ? row.label : '').trim().toLowerCase(),  // Trim and convert to lowercase
          Cell: ({ row }) => (
            <div>
                <button onClick={(e) => handleNodeLinkClick(row.original.id, 'NFT', e)} 
                  className={classes.listTextStyle}
                  style={{ color: row.original.label ? "RGB(0, 255, 0)" : "RGB(255, 0, 0)" }}>
                  {row.original.label}
                </button>
            </div>
          ),
        }, 
        {
          id: 'likes',
          Header: () => (
            <div style={{ textAlign: 'right' }}>
              <span className={classes.listTextStyle}>Likes</span>
            </div>
          ),
          accessor: 'likes',
          Cell: ({ row }) => (
            <div className={classes.tableCellNumber}>
              <button onClick={(e) => handleNodeLinkClick(row.original.id, 'NFT', e)} className={classes.listTextStyle} style={{ maxWidth: '125px' }}>
                {row.original.likes ? row.original.likes : '-'}
              </button>
            </div>
          ),
        },
        {
          id: 'count',
          Header: () => (
            <div style={{ textAlign: 'right' }}>
              <span className={classes.listTextStyle}>Count</span>
            </div>
          ),
          accessor: 'count',
          Cell: ({ row }) => (
            <div className={classes.tableCellNumber}>
                <button onClick={(e) => handleNodeLinkClick(row.original.id, 'NFT', e)} className={classes.listTextStyle} style={{ maxWidth: '100px'}}>
                  {row.original.count}
                </button>
            </div>
          ),
        },         
      ],
      [classes, watchedKeys, toggleWatch, normalizeAddress, hiddenNodeIds, onToggleNodeHidden, graphData]
    );
  
    const PCAPColumns = React.useMemo(
      () => [
          {
            id: 'icon',
            Header: () => (
              <div style={{ textAlign: 'right', marginLeft: '12px' }}>
                <span className={classes.listTextStyle}>&#x1F60A;</span>
              </div>
            ),
            accessor: (row) => {
              try {
                if (!hiddenNodeIds || typeof hiddenNodeIds.has !== 'function') return 0;
                const id = row && row.id != null ? String(row.id) : '';
                if (!id) return 0;
                return hiddenNodeIds.has(id) ? 1 : 0;
              } catch (e) {
                return 0;
              }
            },
            Cell: ({ row }) => {
              const iconSrc = row && row.original && row.original.icon ? String(row.original.icon).trim() : '';
              const knownFailedIcon = !!iconSrc && failedIconUrlsRef.current.has(iconSrc);
              const [imageFailed, setImageFailed] = React.useState(knownFailedIcon);
              const debugLoggedRef = React.useRef(false);
              const isHidden = (() => {
                try {
                  if (!hiddenNodeIds || typeof hiddenNodeIds.has !== 'function') return false;
                  const id = row && row.original && row.original.id != null ? String(row.original.id) : '';
                  if (!id) return false;
                  return hiddenNodeIds.has(id);
                } catch (e) {
                  return false;
                }
              })();
              const toggleHidden = (e) => {
                if (!onToggleNodeHidden) return false;
                try {
                  e.preventDefault();
                  e.stopPropagation();
                } catch (err) {
                }
                try {
                  const id = row && row.original && row.original.id != null ? String(row.original.id) : '';
                  if (id && typeof onToggleNodeHidden === 'function') onToggleNodeHidden(id);
                } catch (err) {
                }
                return true;
              };

              try {
                if (!debugLoggedRef.current && typeof window !== 'undefined' && window.__DEBUG_PCAP_ICONS__) {
                  const icon = row && row.original ? row.original.icon : null;
                  if (icon) {
                    debugLoggedRef.current = true;
                    console.log('[PCAP][icon][nodelist] row has icon', {
                      id: row.original && row.original.id,
                      label: row.original && row.original.label,
                      icon,
                    });
                  }
                }
              } catch (e) {
              }

              React.useEffect(() => {
                setImageFailed(knownFailedIcon);
              }, [iconSrc, knownFailedIcon]);

              const displayIconSrc = iconSrc && (imageFailed || knownFailedIcon) ? genericTokenIcon : iconSrc;

              return (
                <div className={classes.iconColumn} style={{ textAlign: 'center', marginLeft: '4px' }}>
                    {!!displayIconSrc && (
                      <img
                        src={displayIconSrc}
                        alt=""
                        className={classes.iconColumn}
                        style={{ textAlign: 'center', marginLeft: '4px', opacity: isHidden ? 0.25 : 1 }}
                        onClick={(e) => {
                          const handled = toggleHidden(e);
                          if (!handled) handleNodeLinkClick(row.original.id, 'address', e);
                        }}
                        onMouseDown={(e) => { try { e.preventDefault(); e.stopPropagation(); } catch (err) {} }}
                        onTouchStart={(e) => { try { e.preventDefault(); e.stopPropagation(); } catch (err) {} }}
                        loading="lazy"
                        onError={(e) => {
                          try {
                            if (iconSrc) failedIconUrlsRef.current.add(iconSrc);
                            if (typeof window !== 'undefined' && window.__DEBUG_PCAP_ICONS__) {
                              console.warn('[PCAP][icon][nodelist] image failed', {
                                id: row.original && row.original.id,
                                label: row.original && row.original.label,
                                icon: row.original && row.original.icon,
                              });
                            }
                            if (e && e.currentTarget && e.currentTarget.src !== genericTokenIcon) {
                              e.currentTarget.src = genericTokenIcon;
                              return;
                            }
                          } catch (e) {
                          }
                          setImageFailed(true);
                        }}
                      />
                    )}
                    {(!row.original.icon || imageFailed) && useColorDots && row.original.color && (
                      <span
                        style={{
                          display: 'inline-block',
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          backgroundColor: row.original.color,
                          opacity: isHidden ? 0.25 : 1,
                          cursor: onToggleNodeHidden ? 'pointer' : 'default',
                        }}
                        onClick={(e) => { toggleHidden(e); }}
                        onMouseDown={(e) => { try { e.preventDefault(); e.stopPropagation(); } catch (err) {} }}
                        onTouchStart={(e) => { try { e.preventDefault(); e.stopPropagation(); } catch (err) {} }}
                      />
                    )}
                </div>
              );
            },
          },
          {
            id: 'label',
            Header: () => (
              <div>
                <span className={classes.listTextStyle}>Address</span>
              </div>
            ),
            accessor: 'label',
            Cell: ({ row }) => (
              <div style={isMobile && isPortrait ? { maxWidth: viewGraph ? '240px' : '180px', width: viewGraph ? '240px' : '180px' } : !viewGraph ? { maxWidth: '400px', width: '400px' } : {  maxWidth: '150px', width: '150px'}}> 
                <button onClick={(e) => handleNodeLinkClick(row.original.id, 'address', e)}
                  className={classes.listTextStyle}
                >
                  {(() => {
                    const macOn = !!pcapShowMac;
                    const disp = row && row.original && row.original.displayId ? String(row.original.displayId) : '';
                    if (macOn && disp) return disp;
                    return (showNames === false) ? (row.original.id || '') : (row.original.label || row.original.id || '');
                  })()}
                </button>
              </div>
            ),
          },
          {
            id: 'total',
            Header: () => (
              <div style={{ textAlign: 'right' }}>
                <span className={classes.listTextStyle}>Bytes</span>
              </div>
            ),
            accessor: 'totalBytes',
            Cell: ({ row }) => (
              <div style={{ textAlign: 'right', width: '75px' }} > 
                <button onClick={(e) => handleNodeLinkClick(row.original.id, 'address', e)}
                  className={classes.listTextStyle}
                >
                  {row.original.totalBytes ? row.original.totalBytes : '-'}
                </button>
              </div>
            ),
          },
          {
            id: 'sent',
            Header: () => (
              <div style={{ textAlign: 'right' }}>
                <span className={classes.listTextStyle}>Tx Bytes</span>
              </div>
            ),
            accessor: 'sentBytes',
            Cell: ({ row }) => (
              <div style={{ textAlign: 'right', width: '75px' }} > 
                <button onClick={(e) => handleNodeLinkClick(row.original.id, 'address', e)}
                  className={classes.listTextStyle}
                >
                  {row.original.sentBytes ? row.original.sentBytes : '-'}
                </button>
              </div>
            ),
          },
          {
            id: 'received',
            Header: () => (
              <div>
                <span className={classes.listTextStyle}>Rx Bytes</span>
              </div>
            ),
            accessor: 'receivedBytes',
            Cell: ({ row }) => (
              <div style={{ textAlign: 'right', width: '75px' }} > 
                <button onClick={(e) => handleNodeLinkClick(row.original.id, 'address', e)}
                  className={classes.listTextStyle}
                >
                  {row.original.receivedBytes ? row.original.receivedBytes : '-'}
                </button>
              </div>
            ),
          }, 
        ],
      [classes, isMobile, isPortrait, viewGraph, showNames, pcapShowMac, hiddenNodeIds, onToggleNodeHidden, graphData, useColorDots]
    );

    // Define the columns based on the tokenType. In most graph views we
    // previously hid the Market Cap column, but for the MarketCap tokenView
    // we explicitly want that column visible even when viewGraph is true.
    const columns = React.useMemo(
      () =>
        isUploadsVariant
          ? UploadColumns
          : dataSource == PCAP
          ? PCAPColumns
          : tokenType === "Token"
            ? (() => {
                const graphColumns = isGraphsView
                  ? TokenColumns.filter((column) => {
                      const columnId = String(column && (column.id || column.accessor) || '');
                      return !['address', 'storyCount', 'thesisCount'].includes(columnId);
                    })
                  : TokenColumns;

                if (viewGraph && tokenView !== 'marketcap') {
                  return graphColumns.filter(column => column.accessor !== 'marketCap');
                }

                return graphColumns;
              })()
            : NFTColumns,
      [
        isUploadsVariant,
        tokenType,
        viewGraph,
        isGraphsView,
        dataSource,
        tokenView,
        rangeSelected,
        organicLayout,
        showPriceColumns,
        graphData,
        watchedKeys,
        hiddenNodeIds,
        TokenColumns,
        NFTColumns,
        PCAPColumns,
        UploadColumns,
      ]
    );

    const tokens = React.useMemo(
      () => {
        const nodeTypeById = new Map();
        const degreeById = new Map();
        const nodeIconById = new Map();
        const nodeIdByIndex = new Map();
        if (graphData && Array.isArray(graphData.nodes)) {
          graphData.nodes.forEach((n) => {
            const id = n && n.id ? String(n.id).toLowerCase() : '';
            if (!id) return;
            const t = n.metaType || n.nodeType || n.kind || n.type || '';
            if (t && !nodeTypeById.has(id)) nodeTypeById.set(id, String(t));
            if (n && n.icon && !nodeIconById.has(id)) nodeIconById.set(id, n.icon);
          });

          graphData.nodes.forEach((n, idx) => {
            const id = n && n.id ? String(n.id).toLowerCase() : '';
            if (!id) return;
            nodeIdByIndex.set(idx, id);
          });
        }

        if (graphData && Array.isArray(graphData.links)) {
          const resolveEndpointId = (ep) => {
            if (ep == null) return '';
            if (typeof ep === 'string') {
              const s = ep;
              if (/^\d+$/.test(s)) {
                const idx = Number(s);
                const mapped = nodeIdByIndex.get(idx);
                return mapped ? String(mapped) : s;
              }
              return s;
            }
            if (typeof ep === 'number') {
              return nodeIdByIndex.get(ep) || '';
            }
            if (typeof ep === 'object') {
              if (ep.id != null) return ep.id;
              if (ep.index != null) return nodeIdByIndex.get(ep.index) || '';
              if (ep.idx != null) return nodeIdByIndex.get(ep.idx) || '';
            }
            return '';
          };

          graphData.links.forEach((link) => {
            const rawSource = resolveEndpointId(link && link.source);
            const rawTarget = resolveEndpointId(link && link.target);

            const s = rawSource ? String(rawSource).toLowerCase() : '';
            const t = rawTarget ? String(rawTarget).toLowerCase() : '';
            if (s) degreeById.set(s, (degreeById.get(s) || 0) + 1);
            if (t) degreeById.set(t, (degreeById.get(t) || 0) + 1);
          });
        }

        return tokenListCopy
          .filter((token) => {
            if ((tokenView === 'links' || tokenView === 'graphs') && viewGraph) return true;
            if (tokenView === 'price' || tokenView === 'volatility' || tokenView === 'marketcap') return true;
            return showAddressNodes || !token.label.startsWith('0x');
          })
          .map((token) => {
            const idLc = token && token.id ? String(token.id).toLowerCase() : '';
            const linksFromMap = linkCount && typeof linkCount.get === 'function' ? (linkCount.get(idLc) || 0) : 0;
            const links = linksFromMap || (degreeById.get(idLc) || 0);
            const fallbackType = nodeTypeById.get(idLc) || '';
            const icon = token.icon || nodeIconById.get(idLc) || null;
            const inferredType = icon ? 'token' : (idLc && idLc.startsWith('0x') ? 'wallet' : '');
            const type = token.type || fallbackType || inferredType;
            return {
              ...token,
              icon: icon || null,
              links,
              type,
            };
          });
      },
      [tokenListCopy, linkCount, showAddressNodes, graphData]
    );

    const filteredTokens = React.useMemo(() => {
      if (!watchlistOnly) return tokens;

      return (tokens || []).filter((t) => {
        const addr = normalizeAddress(t && t.id);
        if (!addr) return false;
        const chain = normalizeWatchChain(t && t.chain ? String(t.chain) : chainFromDataSource(dataSourceRef.current) || 'ETH');
        const key = `${chain}:${addr}`;
        return watchedKeys && watchedKeys.has(key);
      });
    }, [tokens, watchlistOnly, watchedKeys, chainFromDataSource, normalizeAddress]);

    const initialState = React.useMemo(() => ({
      ...(isUploadsVariant
        ? { sortBy: [{ id: 'created_at', desc: true }] }
        : {}),
      hiddenColumns: Array.isArray(priceHiddenColumns) ? priceHiddenColumns : [],
      // For token lists:
      // - In graph views (/token, user graph), sort by Volume (links) in
      //   Transactions mode, by Price in Price mode, and by the active
      //   change_* column in Volatility mode.
      // - In list views (/price), sort by the selected range's price change.
      // - In /stories inline forensics, preserve the caller-provided row
      //   order exactly (primary first, then icon/name, then others), so do
      //   not set any default sort.
      // Keep existing behavior for other token types.
      sortBy: isUploadsVariant
        ? [{ id: 'created_at', desc: true }]
        : tokenType === "Token"
        ? (tokenView === 'stories'
            ? []
            : viewGraph
              ? (tokenView === 'price'
                  ? [{ id: 'price', desc: true }]
                  : tokenView === 'volatility'
                    ? [{ id: `change_${rangeSelected || '12H'}`, desc: true }]
                    : [{ id: 'links', desc: true }])
              : (tokenView === 'price'
                  ? [{ id: 'marketCap', desc: true }]
                  : [{ id: `change_${rangeSelected || '12H'}`, desc: true }]))
        : [{ id: 'label', asc: true }]
    }), [priceHiddenColumns, showChainColumn, tokenType, viewGraph, rangeSelected, tokenView, isUploadsVariant, selectedPriceTrendInterval]);

    const isServerSortedPriceList = !isUploadsVariant && !!serverPagingEnabled && tokenView === 'price' && !viewGraph && typeof onServerSortChange === 'function';
    const serverSortState = React.useMemo(() => {
      const id = serverSortBy || 'storyCount';
      const desc = String(serverSortDir || 'desc').toLowerCase() !== 'asc';
      return [{ id, desc }];
    }, [serverSortBy, serverSortDir]);

    const handleHeaderSortClick = (column, event) => {
      try {
        if (event && typeof event.preventDefault === 'function') event.preventDefault();
        if (event && typeof event.stopPropagation === 'function') event.stopPropagation();
      } catch (err) {
      }

      if (!column || !column.id) return;

      if (isServerSortedPriceList && typeof onServerSortChange === 'function') {
        const currentId = serverSortBy || 'marketCap';
        const currentDir = String(serverSortDir || 'desc').toLowerCase();
        const nextDir = (column.id === currentId)
          ? (currentDir === 'desc' ? 'asc' : 'desc')
          : 'desc';
        onServerSortChange(column.id, nextDir);
        return;
      }

      const toggleSortBy = headerSortToggleRef.current[column.id] || column.toggleSortBy;
      if (typeof toggleSortBy === 'function') {
        toggleSortBy();
      }
    };

    // Debug: log which columns this NodeList instance is giving to React Table
    // for each render, so we can see modes vs. visible headers.
    /*
    console.log('NodeList columns for render', {
      tokenView,
      viewGraph,
      columnIds: columns.map(c => c.id || c.accessor),
      accessors: columns.map(c => c.accessor),
    });
    */

    const tableData = isUploadsVariant ? uploadsData : filteredTokens;

    const {
      getTableProps,
      getTableBodyProps, 
      headerGroups,
      rows,
      prepareRow,
      setHiddenColumns,
    } = useTable({ 
      columns, 
      data: tableData, 
      initialState: isServerSortedPriceList ? { ...initialState, sortBy: serverSortState } : initialState,
      manualSortBy: isServerSortedPriceList,
      autoResetSortBy: false,
      disableSortRemove: true,
    }, useSortBy); 

    useEffect(() => {
      if (typeof onVisibleCountChange !== 'function') return;
      onVisibleCountChange(Array.isArray(rows) ? rows.length : 0);
      return () => {
        try {
          onVisibleCountChange(null);
        } catch (err) {
        }
      };
    }, [onVisibleCountChange, rows.length]);

    useEffect(() => {
      if (typeof setHiddenColumns !== 'function') return;
      setHiddenColumns(Array.isArray(priceHiddenColumns) ? priceHiddenColumns : []);
    }, [priceHiddenColumns, setHiddenColumns]);

    useEffect(() => {
      if (!isServerSortedPriceList) return;
      // In virtualized mode, scrolling happens inside react-window (we use
      // onItemsRendered below). Keep this listener as a fallback only.
      const el = nodeListRef.current;
      if (!el) return;

      const onScroll = () => {
        try {
          if (typeof onLoadMore !== 'function') return;
          const thresholdPx = 400;
          const nearBottom = (el.scrollTop + el.clientHeight) >= (el.scrollHeight - thresholdPx);
          if (nearBottom) {
            onLoadMore();
          }
        } catch (e) {
        }
      };

      el.addEventListener('scroll', onScroll);
      return () => el.removeEventListener('scroll', onScroll);
    }, [isServerSortedPriceList, onLoadMore]);

    useImperativeHandle(ref, () => ({
      scrollToActiveNode,
      nodeRefs,
      getSortedRows: () => rows
    }), [rows]);

    //console.log('SortBy State:', initialState.sortBy);

    const highlightRowStyle = { backgroundColor: '#A9A9A9' }; // Replace with your desired highlight style

    const [virtualListHeight, setVirtualListHeight] = useState(600);

    useEffect(() => {
      if (!isServerSortedPriceList) return;
      const el = nodeListRef.current;
      if (!el || typeof ResizeObserver === 'undefined') return;

      const ro = new ResizeObserver(() => {
        try {
          const h = el.clientHeight;
          if (h && Number.isFinite(h)) {
            setVirtualListHeight(h);
          }
        } catch (e) {
        }
      });
      ro.observe(el);
      return () => ro.disconnect();
    }, [isServerSortedPriceList]);

    const handleNodeLinkClick = (id, type, e) => {
      if (isUploadsVariant) return;
      
      // In /graphs or /links view, navigate to token intelligence page
      if (isGraphOrLinkView) {
        const address = String(id || '').trim();
        if (address && address.startsWith('0x')) {
          // Use window.location for immediate navigation without React Router delays
          window.location.href = `/token/${address}`;
        }
        return;
      }
      
      // Original behavior for other views
      stopCameraSpin();
      if (e && e.shiftKey && id.startsWith('0x')) {
        getTransactions(dataSourceRef.current, id);
      } else if (type == 'tx')  {
        handleLinkClick(graphDataRef.current, graphRef, id, true);
      } else {
        handleNodeClick(dataSourceRef.current, graphDataRef.current, graphRef, id, true, type);
      }
    };

    const handleRowContextMenu = (row, e) => {
      if (isUploadsVariant) return;
      try {
        if (e && typeof e.preventDefault === 'function') e.preventDefault();
        if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
      } catch (err) {
      }

      try {
        const idRaw = row && row.original ? (row.original.id || row.original.address) : null;
        const id = idRaw != null ? String(idRaw).trim() : '';
        if (!id) return;
        const node = (graphDataRef.current && Array.isArray(graphDataRef.current.nodes))
          ? graphDataRef.current.nodes.find((n) => n && String(n.id || '').trim() === id)
          : null;
        const payloadNode = node || { id };

        window.dispatchEvent(new CustomEvent('open-node-context-menu', {
          detail: {
            mouseX: e && e.clientX,
            mouseY: e && e.clientY,
            node: payloadNode,
            graphDataOverride: graphDataRef.current,
          }
        }));
      } catch (err) {
      }
    };
  
    const NodeListTable = () => {
      return ( 
        <table {...getTableProps()} className={classes.table}>
          <thead>
            {headerGroups.map(headerGroup => (
              (() => {
                const headerGroupProps = headerGroup.getHeaderGroupProps();
                const { key: hgKey, ...restHgProps } = headerGroupProps || {};
                return (
                  <tr key={hgKey} {...restHgProps}>
                {headerGroup.headers.map(column => (
                  (() => {
                    if (column && column.id) {
                      headerSortToggleRef.current[column.id] = column.toggleSortBy;
                    }
                    const headerProps = column.getHeaderProps();

              const { key, ...rest } = headerProps || {};
              const serverIsSorted = isServerSortedPriceList && column.id === (serverSortBy || 'marketCap');
              const serverIsSortedDesc = String(serverSortDir || 'desc').toLowerCase() !== 'asc';
              return (
                      <th
                        key={key}
                        {...rest}
                        className={classes.tableHeader}
                        data-sort-column={column.id}
                        onMouseDownCapture={(e) => handleHeaderSortClick(column, e)}
                        data-column-id={column.id}
                        style={{
                          position: 'sticky',
                          top: 0,
                          backgroundColor: '#000',
                          textAlign: column.id === 'price' || column.id === 'marketCap' || column.id === 'supply' || column.id === 'version' || column.id === 'likes' || column.id === 'count'
                            ? 'right' // Only right-align these columns
                            : 'left',
                          width: column.id === 'fragilityScore' ? '56px' : (String(column.id || '').startsWith('change_') ? '66px' : undefined),
                          minWidth: column.id === 'fragilityScore' ? '56px' : (String(column.id || '').startsWith('change_') ? '66px' : undefined),
                          maxWidth: column.id === 'fragilityScore' ? '56px' : (String(column.id || '').startsWith('change_') ? '66px' : undefined),
                          padding: '0',
                          paddingRight: String(column.id || '').startsWith('change_') ? '8px' : '0',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: column.id === 'price' || column.id === 'marketCap' || column.id === 'supply'  | column.id === 'version' || column.id === 'likes' || column.id === 'count'
                              ? 'flex-end' // Right-align content for these columns
                              : 'flex-start',
                            alignItems: 'center',
                            height: '20px',
                            borderBottom: '1px solid gray',
                            width: '100%',
                            paddingRight: String(column.id || '').startsWith('change_') ? '8px' : '0',
                          }}
                        >
                          {column.render('Header')}
                          {isServerSortedPriceList
                            ? (serverIsSorted ? (serverIsSortedDesc ? ' 🔽' : ' 🔼') : '')
                            : (column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : '')}
                        </div>
                      </th>
                    );
                  })()
                ))}
                  </tr>
                );
              })()
            ))}
          </thead>
          <tbody {...getTableBodyProps()} className={classes.tableBody}>
            {rows.map(row => {
              prepareRow(row);
              const isRowActive = row.original.id === activeNodeId;
              const isRowHidden = (() => {
                try {
                  if (!hiddenNodeIds || typeof hiddenNodeIds.has !== 'function') return false;
                  const id = row && row.original && row.original.id != null ? String(row.original.id) : '';
                  if (!id) return false;
                  return hiddenNodeIds.has(id);
                } catch (e) {
                  return false;
                }
              })();

              const rowProps = row.getRowProps({
                className: isRowActive ? classes.activeRow : classes.tableRow,
                style: {
                  cursor: 'pointer',
                  backgroundColor: hoveredRowId === row.original.id ? 'rgba(255, 255, 255, 0.12)' : undefined,
                  ...(isRowActive ? highlightRowStyle : {}),
                  ...((isRowHidden && !isRowActive) ? { opacity: 0.35, filter: 'grayscale(0.85) brightness(0.85)' } : {}),
                },
                onMouseEnter: () => setHoveredRowId(row.original.id),
                onMouseLeave: () => setHoveredRowId((current) => (current === row.original.id ? null : current)),
                onMouseDownCapture: isGraphOrLinkView ? (e) => handleGraphRowActivation(row.original && row.original.id, e) : undefined,
                onContextMenu: (e) => handleRowContextMenu(row, e),
                ref: element => {
                  if (element && row.original?.id && nodeRefs.current[row.original.id] !== element) {
                    nodeRefs.current[row.original.id] = element;
                  }
                  if (element === null && row.original?.id) {
                    delete nodeRefs.current[row.original.id];
                  }
                },
              });

              const { key: rowKey, ...restRowProps } = rowProps || {};
              return (
                <tr key={rowKey} data-row-id={row.original && row.original.id ? String(row.original.id) : ''} {...restRowProps}>
                  {row.cells.map(cell => {
                    const cellProps = cell.getCellProps();
                    const { key: cellKey, ...restCellProps } = cellProps || {};
                    return (
                      <td key={cellKey} {...restCellProps} className={classes.tableCell}>
                        {cell.render('Cell')}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
    </table>
  );
  };

    const VirtualizedPriceList = () => {
      return <NodeListTable />;
    };
    
    
    return (
      <div
        className={(isMobile && isPortrait) ? classes.nodeListContainerMobile : classes.nodeListContainer}
        id={(isMobile && isPortrait) ? "nodeListContainerMobile" : "nodeListContainer" }
        style={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          ref={nodeListRef}
          tabIndex="-1"
          style={{
            flex: '1 1 auto',
            minHeight: 0,
            overflow: 'auto',
            position: 'relative',
          }}
        >
          {isServerSortedPriceList && priceListBusy && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 50,
                background: 'rgba(0,0,0,0.55)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'auto',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <CircularProgress size={22} style={{ color: '#fff' }} />
                <span className={classes.listTextStyle} style={{ color: '#fff' }}>Loading…</span>
              </div>
            </div>
          )}
          <div style={{ paddingBottom: showTokenViewToggle ? 8 : 0 }}>
            {isServerSortedPriceList && priceListBusy && rows.length > 0 && (
              <div
                style={{
                  position: 'sticky',
                  bottom: 12,
                  zIndex: 60,
                  display: 'flex',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 14px',
                    borderRadius: 999,
                    background: 'rgba(20, 20, 20, 0.92)',
                    border: '1px solid rgba(111, 135, 255, 0.28)',
                    boxShadow: '0 12px 28px rgba(0, 0, 0, 0.35)',
                  }}
                >
                  <CircularProgress size={18} style={{ color: '#fff' }} />
                  <span className={classes.listTextStyle} style={{ color: '#fff' }}>Loading more…</span>
                </div>
              </div>
            )}
            {!isUploadsVariant && watchlistOnly && (!filteredTokens || !filteredTokens.length) ? (
              <div style={{ padding: 12 }}>
                <span className={classes.listTextStyle}>No watched items in this view.</span>
              </div>
            ) : (
              (isServerSortedPriceList ? <VirtualizedPriceList/> : <NodeListTable/>)
            )}
            {showPriceDetails && (
              <PriceDetailsDialog onClose={() => setShowPriceDetails(false)} />
            )}
            {showFragilityScoreDetails && (
              <FragilityScoreDetailsDialog onClose={() => setShowFragilityScoreDetails(false)} />
            )}
          </div>
        </div>

        {showTokenViewToggle && (
          <div
            style={{
              flex: '0 0 auto',
              padding: '10px 10px',
              backgroundColor: 'rgba(0,0,0,0.92)',
              borderTop: '1px solid rgba(255,255,255,0.12)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {includeChainOption && tokenView === 'chain' && typeof onOpenCategories === 'function' && (
              <button
                onClick={() => onOpenCategories()}
                style={{
                  marginRight: 12,
                  color: 'white',
                  border: '1px solid #666',
                  background: 'transparent',
                  textTransform: 'none',
                  fontSize: '11px',
                  padding: '4px 10px',
                  height: '100%',
                  borderRadius: 4,
                  cursor: 'pointer',
                }}
              >
                Categories{(Number(categoriesCount) || 0) > 0 ? ` (${Number(categoriesCount) || 0})` : ''}
              </button>
            )}
            <div
              style={{
                display: 'flex',
                borderRadius: '16px',
                overflow: 'hidden',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                border: '1px solid #444',
              }}
            >
              {[
                ...(includeChainOption ? [{ mode: 'chain', label: 'Chain', color: '#9C27B0', borderRight: true }] : []),
                { mode: 'price',      label: 'Price',      color: '#4CAF50', borderRight: true },
                { mode: 'volatility', label: 'Volatility', color: '#F44336', borderRight: true },
                { mode: 'marketcap',  label: 'MktCap',     color: '#FF9800', borderRight: true },
                { mode: 'links',      label: 'Txs',        color: '#2196F3', borderRight: false },
              ].map((opt) => (
                <button
                  key={opt.mode}
                  onClick={() => {
                    if (typeof onTokenViewChange === 'function') onTokenViewChange(opt.mode);
                  }}
                  style={{
                    padding: '4px 10px',
                    border: 'none',
                    cursor: 'pointer',
                    backgroundColor: tokenView === opt.mode ? opt.color : 'transparent',
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: tokenView === opt.mode ? 'bold' : 'normal',
                    borderRight: opt.borderRight ? '1px solid #444' : 'none',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
});
  

// LinkList - list of transactions
export const LinkList = (props) => {
    const { graphData, graphRef, dataSource, rate, stopCameraSpin, handleNodeClick, handleLinkClick, activeLinkId, activeNodeId, transactions  } = props;
    const classes = useStyles();
    const graphDataRef = useRef(graphData);
    const dataSourceRef = useRef(dataSource);
    const linkRefs = useRef({});
    const linkListRef = useRef();
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
      graphDataRef.current = graphData;
      dataSourceRef.current = dataSource;
    }, [graphData, dataSource]);

    useEffect(() => {
      const preventDefaultKeyDown = (event) => {
          if (event.key === 'ArrowDown') {
              event.preventDefault();
          }
      };
      document.addEventListener('keydown', preventDefaultKeyDown);
      return () => document.removeEventListener('keydown', preventDefaultKeyDown);
   }, []);

    useEffect(() => {
      if (activeLinkId && linkRefs.current[activeLinkId]) {
        scrollToActiveLink(activeLinkId);
      }
    }, [activeLinkId]);

    useEffect(() => {
      const linkListElement = linkListRef.current;
  
      const handleMouseEnter = () => {
          linkListElement.focus();
      };
  
      const handleMouseLeave = () => {
          linkListElement.blur(); // Optional, based on desired behavior
      };
  
      linkListElement.addEventListener('mouseenter', handleMouseEnter);
      linkListElement.addEventListener('mouseleave', handleMouseLeave);
  
      return () => {
          linkListElement.removeEventListener('mouseenter', handleMouseEnter);
          linkListElement.removeEventListener('mouseleave', handleMouseLeave);
      };
    }, []);


    useEffect(() => {
        //console.log("Effect running", { activeNodeId, rowsLength: rows.length });

        const handleKeyDown = (event) => {
            if ((event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
                // Check if the nodeListContainer is currently focused
                if (document.activeElement === linkListRef.current) {
                    const currentIndex = rows.findIndex(row => row.original.blockNumber === activeLinkId);
                    const newIndex = (event.key === 'ArrowDown') ? currentIndex + 1 : currentIndex - 1;
    
                    // Ensure newIndex is within bounds
                    if (newIndex >= 0 && newIndex < rows.length) {
                        setCurrentIndex(newIndex);
                        const newActiveLinkId = rows[newIndex].original.id;
                        
                        // Update the active node
                        handleLinkClick(dataSourceRef.current, graphDataRef.current, graphRef, newActiveLinkId, false);
                        
                        // Scroll to the new node if it's not visible
                        if (!isRowVisible(newIndex)) {
                            scrollToActiveLink(newActiveLinkId);
                        }
                    }
                }
            }
        };
      
        document.addEventListener('keydown', handleKeyDown);
 
        return () => {
          document.removeEventListener('keydown', handleKeyDown);
        };
    }, [activeLinkId, handleLinkClick, dataSourceRef, graphDataRef, graphRef, setCurrentIndex ]); 

  
    const scrollToActiveLink = (txHash) => {
      const element = linkRefs.current[txHash];
    
      if (element && !isInViewport(element)) {
        const scrollOptions = {
          behavior: 'smooth',
          block: 'center', 
          inline: 'nearest'
        };
    
        element.scrollIntoView(scrollOptions);
      }
    };

    const isInViewport = (element) => {
        const rect = element.getBoundingClientRect();
        return rect.top >= 0 && rect.bottom <= window.innerHeight;
    };

    const isRowVisible = (rowIndex) => {
        const rowElement = linkRefs.current[rows[rowIndex].original.blockNumber];
        if (!rowElement || !linkListRef.current) return false;
    
        const listRect = linkListRef.current.getBoundingClientRect();
        const rowRect = rowElement.getBoundingClientRect();
    
        // Check if the row is within the nodeListContainer's visible bounds
        return rowRect.top >= listRect.top && rowRect.bottom <= listRect.bottom;
    };
  
    const onClick = (original, type, e) => {
      const id = original.tx;
      stopCameraSpin();
      if (e.shiftKey && id.startsWith('0x') && dataSource > 0) {
        const explorerBaseUrl = (dataSource == ETHERSCAN) ? 'https://etherscan.io' : 'https://bscscan.com';
        const url = explorerBaseUrl + '/' + type + '/' + id;
        window.open(url, '_blank');
      } else if (type == 'tx')  {
        handleLinkClick(dataSourceRef, graphDataRef.current, graphRef, id, true);
      } else {
        handleNodeClick(dataSourceRef, graphDataRef.current, graphRef, id, true);
      }
    };

  
    function formatValueEth(valueWei, ethToUsdRate) {
      const valueEth = convertWeiToEth(valueWei).toFixed(6);
      const valueUsd = (valueEth * ethToUsdRate).toLocaleString('en-US', {style: 'currency', currency: 'USD'});
      const value = `${valueEth} ETH `;
      return value;
    }

    function formatValueUSD(valueWei, ethToUsdRate) {
      const valueEth = convertWeiToEth(valueWei).toFixed(6);
      const valueUsd = (valueEth * ethToUsdRate).toLocaleString('en-US', {style: 'currency', currency: 'USD'});
      const value = `(${valueUsd})`;
      return value;
    }

    function convertWeiToEth(wei) {
      return wei / 1e18;
    }
   
    function formatWeiToGwei(wei) {
      const weiInOneEth = 1e18; // 1,000,000,000,000,000,000 Wei in one Ether
      const weiInOneGwei = 1e9; // 1,000,000,000 Wei in one Gwei
      // Convert Wei to Gwei and ETH
      const gwei = wei / weiInOneGwei; // Convert Wei to Gwei
      const eth = wei / weiInOneEth; // Convert Wei to Ether
      // Format to a fixed number of decimal places for readability
      const formattedGwei = gwei.toFixed(9); // Gwei usually does not require many decimal places
      const value = `${formattedGwei} Gwei `; 
      return value; 
    }

    function formatWeiToEth(wei) {
      const weiInOneEth = 1e18; // 1,000,000,000,000,000,000 Wei in one Ether
      const weiInOneGwei = 1e9; // 1,000,000,000 Wei in one Gwei
      // Convert Wei to Gwei and ETH
      const gwei = wei / weiInOneGwei; // Convert Wei to Gwei
      const eth = wei / weiInOneEth; // Convert Wei to Ether
      // Format to a fixed number of decimal places for readability
      const formattedGwei = gwei.toFixed(9); // Gwei usually does not require many decimal places
      const formattedEth = eth.toFixed(18); // ETH can be very small, so keeping all decimal places
      const value = `(${formattedEth} ETH)`;
      return value; 
    }

    function formatTransactionCostEth(gasLimit, gasPrice) {
      const gasLimitNum = Number(gasLimit);
      const gasPriceNum = Number(gasPrice); // Assuming gasPrice is already in wei
      // No need to convert gasPrice from gwei to wei
      const totalGasCostInWei = gasLimitNum * gasPriceNum;
      const totalGasCostInEther = totalGasCostInWei / 1e18; // Convert wei to ether
      const value = `${totalGasCostInEther.toFixed(6)} ETH `;
      return value;
    }
  
    function formatTransactionCostUSD(gasLimit, gasPrice, currentEthRate) {
      const gasLimitNum = Number(gasLimit);
      const gasPriceNum = Number(gasPrice); // Assuming gasPrice is already in wei
      const ethRateNum = Number(currentEthRate);
      // No need to convert gasPrice from gwei to wei
      const totalGasCostInWei = gasLimitNum * gasPriceNum;
      const totalGasCostInEther = totalGasCostInWei / 1e18; // Convert wei to ether
      const totalGasCostInUSD = totalGasCostInEther * ethRateNum;
      const value = `($${totalGasCostInUSD.toFixed(2)})`;
      return value;
    }

    function convertUTCToLocalAsTimestamp(utcDateString) {
      // Append 'Z' to the date string to indicate that it's in UTC time
      const utcDateWithZ = utcDateString + 'Z';
      // Create a date object using the updated string
      const date = new Date(utcDateWithZ);
      // Convert to local time string in Pacific Time
      const localTimeString = date.toLocaleString("en-US", { timeZone: "America/Los_Angeles" });
      return localTimeString;
    }
    

    const columns = React.useMemo(
      () => [
        {
          Header: 'Timestamp',
          accessor: 'timestamp',
          Cell: ({ row }) => (
            <div style={{ display: 'flex', alignItems: 'left' }}>
               <button onClick={(e) => onClick(row.original, 'tx', e)} className={classes.linkButtonStyle}>
                {convertUTCToLocalAsTimestamp(row.original.timestamp)}
              </button>
            </div>
          ),
        },
        {
          Header: 'Type',
          accessor: 'txType',
          Cell: ({ row }) => (
            <div style={{ display: 'flex', alignItems: 'left' }}>
              <button onClick={(e) => onClick(row.original, 'tx', e)} className={classes.linkButtonStyle}>
                {row.original.txType === '0' ? 'Swap' : row.original.txType}
              </button>
            </div>
          ),
        },
        {
          Header: 'From',
          accessor: 'source.label',
          Cell: ({ row }) => (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <button onClick={(e) => onClick(row.original, 'tx', e)} className={classes.linkButtonStyle} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <img src={(row.original.fromIcon) ? row.original.fromIcon : genericUserIcon} alt="" className={classes.iconColumnSmall} />
                {formatShortAddress(row.original.source)}
              </button>
            </div>
          ),
        },
        {
          Header: 'To',
          accessor: 'target.label',
          Cell: ({ row }) => (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <button onClick={(e) => onClick(row.original, 'tx', e)} className={classes.linkButtonStyle} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <img src={(row.original.toIcon) ? row.original.toIcon : genericUserIcon} alt="" className={classes.iconColumnSmall} />
                {formatShortAddress(row.original.target)}
              </button>
            </div>
          ),
        },
        {
          Header: 'Block#',
          accessor: 'blockNumber',
          Cell: ({ row }) => (
            <div style={{ display: 'flex', alignItems: 'left' }}>
              <button onClick={(e) => onClick(row.original, 'tx', e)} className={classes.linkButtonStyle}>
                {row.original.blockNumber}
              </button>
            </div>
          ),
        },
        {
          Header: 'Transaction',
          accessor: 'tx',
          Cell: ({ row }) => (
            <button onClick={(e) => onClick(row.original, 'tx', e)} className={classes.linkButtonStyle}>
              {formatShortAddress(row.original.tx)}
            </button>
          ),
        },
        {
          Header: 'Value',
          accessor: 'value',
          Cell: ({ row }) => (
            <div>
              <button onClick={(e) => onClick(row.original, 'tx', e)} className={classes.linkButtonStyle}>
                {formatValueEth(row.original.value,rate)}
              </button>
              <button onClick={(e) => onClick(row.original, 'tx', e)} className={classes.goldButtonStyle}>
                {formatValueUSD(row.original.value,rate)}
              </button>
            </div>
          ),
        },
        {
          Header: 'Transaction Fee',
          accessor: 'fee',
          Cell: ({ row }) => (
            <div>
              <button onClick={(e) => onClick(row.original, 'tx', e)} className={classes.linkButtonStyle}>
                {formatTransactionCostEth(row.original.gasLimit, row.original.gasPrice)}
              </button>
              <button onClick={(e) => onClick(row.original, 'tx', e)} className={classes.goldButtonStyle}>
                {formatTransactionCostUSD(row.original.gasLimit, row.original.gasPrice, rate)}
              </button>
            </div>
          ),
        },
        {
          Header: 'Gas Limit',
          accessor: 'gasLimit',
          Cell: ({ row }) => (
            <button onClick={(e) => onClick(row.original, 'tx', e)} className={classes.linkButtonStyle}>
              {row.original.gasLimit}
            </button>
          ),
        },
        {
          Header: 'Gas Used',
          accessor: 'gasUsed',
          Cell: ({ row }) => (
            <button onClick={(e) => onClick(row.original, 'tx', e)} className={classes.linkButtonStyle}>
              {row.original.gasUsed}
            </button>
          ),
        },
        {
          Header: 'Gas Price',
          accessor: 'gasPrice',
          Cell: ({ row }) => (
            <div>
              <button onClick={(e) => onClick(row.original, 'tx', e)} className={classes.linkButtonStyle}>
                {formatWeiToGwei(row.original.gasPrice)}
              </button>
              <button onClick={(e) => onClick(row.original, 'tx', e)} className={classes.goldButtonStyle}>
                {formatWeiToEth(row.original.gasPrice)}
              </button>
            </div>
          ),
        },

        // ... other columns ...
      ],
      [classes.linkButtonStyle]
    );
  
    // Move useMemo outside the conditional to comply with React Hooks rules
    const data = React.useMemo(() => { 
      // Handle the case where transactions is null/undefined
      if (!transactions) return [];
      
      // Filter transactions if they exist
      return transactions.filter(tx => (
        (tx.txType != '1' && tx.txType != '2' && tx.txType != '') && 
        (!activeNodeId || (tx.source == activeNodeId || tx.target == activeNodeId))
      ));
    }, [transactions, activeNodeId]);

    const highlightRowStyle = { backgroundColor: '#A9A9A9' }; // Replace with your desired highlight style

    // Use the useTable Hook to send the columns and data to build the table
    const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      rows,
      prepareRow,
    } = useTable({ columns, data }, useSortBy);
  
    return (
      <div ref={linkListRef}  tabIndex="-1" className={classes.linkListContainer}>
        <table {...getTableProps()} className={classes.table}>
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())} className={classes.tableHeader}>
                    <div style={{ display: 'flex', alignItems: 'center', height: '20px', borderBottom: '1px solid gray' }}>
                      {column.render('Header')}
                      {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()} className={classes.tableBody}>
            {rows.map(row => {
              prepareRow(row);
              const isRowActive = row.original.tx === props.activeLinkId;
              return (
                <tr {...row.getRowProps({ 
                  className: isRowActive ? classes.activeRow : classes.tableRow,
                  style: isRowActive ? highlightRowStyle : {},
                  ref: element => {
                    // Store a ref for each row
                    if (element && row.original) {
                      linkRefs.current[row.original.tx] = element;
                    }
                  } })}>
                  {row.cells.map(cell => {
                    return (
                      <td {...cell.getCellProps()} className={classes.tableCell}>
                        {cell.render('Cell')}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };
  
  
export const LinkListPCAP = (props) => {
    const { graphData, conversations, graphRef, mapData, pcapMacOuiByMac, stopCameraSpin, handleNodeClick, onSelectConversation, showNames, pcapShowMac, pcapShowOui } = props;
    const classes = useStyles();
    const pcapPortsDebugRef = useRef(0);
    const [sortState, setSortState] = useState({ key: null, dir: 'asc' });

    const isIPv4 = (s) => /^\d{1,3}(?:\.\d{1,3}){3}$/.test(String(s || '').trim());
    const isMacAddress = (s) => /^([0-9A-Fa-f]{2}([:-])){5}[0-9A-Fa-f]{2}$/.test(String(s || '').trim());

    const normalizeEndpointId = (v) => {
      try {
        if (v == null) return '';
        if (typeof v === 'object') {
          // IMPORTANT: only use stable identifiers here. Do NOT use label/name,
          // since that may include friendly names and will break IP/MAC detection.
          const raw = v.id ?? v._id ?? v.address;
          if (raw != null) return String(raw).trim();
          // Safe fallback: if label itself is a literal address, use it.
          const lbl = v.label != null ? String(v.label).trim() : '';
          if (lbl && (isIPv4(lbl) || isMacAddress(lbl))) return lbl;
          return '';
        }
        return String(v).trim();
      } catch (e) {
        return '';
      }
    };

    const canonicalMac = (raw) => {
      const s = String(raw || '').trim();
      if (!s) return '';
      const hex = s.replace(/[^0-9A-Fa-f]/g, '').toLowerCase();
      if (hex.length !== 12) return '';
      return hex.match(/.{1,2}/g).join(':');
    };

    const buildMacDisplayMap = () => {
      const out = new Map();
      try {
        const nodes = graphData && Array.isArray(graphData.nodes) ? graphData.nodes : [];
        for (let i = 0; i < nodes.length; i += 1) {
          const n = nodes[i];
          if (!n || n.id == null) continue;
          const id = String(n.id).trim();
          if (!isMacAddress(id)) continue;
          const key = canonicalMac(id) || id.toLowerCase();
          const disp = (n.displayId != null && String(n.displayId).trim())
            ? String(n.displayId).trim()
            : (() => {
              const org = (n.label != null ? String(n.label).trim() : '');
              return (pcapShowOui && org && org !== id) ? `${org} (${id})` : id;
            })();
          if (key && disp) out.set(key, disp);
        }

        // Fallback: if MAC nodes are filtered out (MAC toggle off), we can still
        // show OUI vendor names in the bottom list via the unfiltered base map.
        if (pcapShowOui && pcapMacOuiByMac && typeof pcapMacOuiByMac.forEach === 'function') {
          pcapMacOuiByMac.forEach((org, rawMac) => {
            const mac = rawMac != null ? String(rawMac).trim() : '';
            if (!mac || !isMacAddress(mac)) return;
            const key = canonicalMac(mac) || mac.toLowerCase();
            if (out.has(key)) return;
            const vendor = org != null ? String(org).trim() : '';
            if (!vendor) return;
            out.set(key, `${vendor} (${canonicalMac(mac) || mac})`);
          });
        }
      } catch (e) {
      }
      return out;
    };

    const formatMacWithOui = (rawMac, macDisplayByMac) => {
      const mac = String(rawMac || '').trim();
      if (!mac || mac === '-' || !isMacAddress(mac)) return '-';
      const key = canonicalMac(mac) || mac.toLowerCase();
      if (!pcapShowOui) return canonicalMac(mac) || mac;
      if (macDisplayByMac && macDisplayByMac.has(key)) return macDisplayByMac.get(key);

      // Secondary fallback when MAC nodes are filtered out: use the base MAC->OUI map.
      try {
        const canon = canonicalMac(mac) || mac;
        const org = pcapMacOuiByMac && typeof pcapMacOuiByMac.get === 'function' ? pcapMacOuiByMac.get(canon) : null;
        const vendor = org != null ? String(org).trim() : '';
        if (vendor) return `${vendor} (${canon})`;
      } catch (e) {
      }

      // Fallback to mapData lookup if available.
      try {
        const candidates = [mac, mac.toLowerCase(), canonicalMac(mac)];
        for (let i = 0; i < candidates.length; i += 1) {
          const k = candidates[i];
          if (!k) continue;
          const macNode = mapData && typeof mapData.get === 'function' ? mapData.get(k) : null;
          const org = macNode && macNode.label ? String(macNode.label).trim() : '';
          const canon = canonicalMac(mac) || mac;
          if (pcapShowOui && org && org !== mac) return `${org} (${canon})`;
        }
      } catch (e) {
      }
      return mac;
    };

    const buildIdToLabelMap = () => {
      const out = new Map();
      try {
        const nodes = graphData && Array.isArray(graphData.nodes) ? graphData.nodes : [];
        for (let i = 0; i < nodes.length; i += 1) {
          const n = nodes[i];
          if (!n || n.id == null) continue;
          const id = String(n.id).trim();
          if (!id) continue;
          const lbl = n.label != null ? String(n.label).trim() : '';
          if (lbl) out.set(id, lbl);
        }
      } catch (e) {
      }
      return out;
    };

    const buildIdToIconMap = () => {
      const out = new Map();
      try {
        const nodes = graphData && Array.isArray(graphData.nodes) ? graphData.nodes : [];
        for (let i = 0; i < nodes.length; i += 1) {
          const n = nodes[i];
          if (!n || n.id == null) continue;
          const id = String(n.id).trim();
          if (!id) continue;
          const icon = n.icon != null ? String(n.icon).trim() : '';
          if (icon) out.set(id, icon);
        }
      } catch (e) {
      }
      return out;
    };

    const deriveIpToMac = () => {
      const ipToMac = new Map();
      try {
        const links = graphData && Array.isArray(graphData.links) ? graphData.links : [];

        // 1) Prefer mapping derived from the actual conversation links themselves.
        // The PCAP CSV provides Client MAC / Server MAC per row, and csvToGraphData
        // attaches those to the directed pcap links as fromMac/toMac.
        for (let i = 0; i < links.length; i += 1) {
          const l = links[i];
          if (!l || l.metaType !== 'pcap') continue;
          const sId = normalizeEndpointId(l.source);
          const tId = normalizeEndpointId(l.target);
          const s = sId != null ? String(sId).trim() : '';
          const t = tId != null ? String(tId).trim() : '';
          const fm = l && l.fromMac ? String(l.fromMac).trim() : '';
          const tm = l && l.toMac ? String(l.toMac).trim() : '';
          if (isIPv4(s) && fm && isMacAddress(fm) && !ipToMac.has(s)) ipToMac.set(s, canonicalMac(fm) || fm);
          if (isIPv4(t) && tm && isMacAddress(tm) && !ipToMac.has(t)) ipToMac.set(t, canonicalMac(tm) || tm);

          // Also consume collapsed/grouped endpoint MACs if present.
          const ma = l && l.macA ? String(l.macA).trim() : '';
          const mb = l && l.macB ? String(l.macB).trim() : '';
          if (isIPv4(s) && ma && isMacAddress(ma) && !ipToMac.has(s)) ipToMac.set(s, canonicalMac(ma) || ma);
          if (isIPv4(t) && mb && isMacAddress(mb) && !ipToMac.has(t)) ipToMac.set(t, canonicalMac(mb) || mb);
        }

        // 2) Fill any remaining gaps using helper MAC↔IP links.
        for (let i = 0; i < links.length; i += 1) {
          const l = links[i];
          if (!l || l.metaType !== 'pcap_mac') continue;
          const s = normalizeEndpointId(l.source);
          const t = normalizeEndpointId(l.target);
          const sId = s != null ? String(s).trim() : '';
          const tId = t != null ? String(t).trim() : '';
          if (isMacAddress(sId) && isIPv4(tId) && !ipToMac.has(tId)) ipToMac.set(tId, canonicalMac(sId) || sId);
          else if (isMacAddress(tId) && isIPv4(sId) && !ipToMac.has(sId)) ipToMac.set(sId, canonicalMac(tId) || tId);
        }
      } catch (e) {
      }
      return ipToMac;
    };

    const displayEndpoint = (id, macDisplayByMac) => {
      const raw = id != null ? String(id).trim() : '';
      if (!raw) return '-';
      if (showNames === false) return raw;
      if (isMacAddress(raw)) return formatMacWithOui(raw, macDisplayByMac);
      try {
        // Prefer labels from graphData.nodes (PCAP name-table updates land there).
        const lblFromGraph = idToLabel && idToLabel.get(raw) ? String(idToLabel.get(raw)).trim() : '';
        if (lblFromGraph && lblFromGraph !== raw) return `${lblFromGraph} (${raw})`;

        // Fallback: mapData (can lag behind graphData depending on update path)
        const node = mapData && typeof mapData.get === 'function' ? mapData.get(raw) : null;
        const lbl = node && node.label ? String(node.label).trim() : '';
        if (lbl && lbl !== raw) return `${lbl} (${raw})`;
        return raw;
      } catch (e) {
      }
      return raw;
    };

    useEffect(() => {
      try {
        const links = graphData && Array.isArray(graphData.links) ? graphData.links : [];
        // Log sparingly: only a few times per session and only when link count changes.
        if (!links.length) return;
        const sig = links.length;
        if (pcapPortsDebugRef.current === sig) return;
        pcapPortsDebugRef.current = sig;

        const sample = links.slice(0, 8).map((l) => {
          const ports = l && l.ports ? l.ports : null;
          const flows = l && l.pcapFlows && typeof l.pcapFlows === 'object' ? l.pcapFlows : null;
          const keys = flows ? Object.keys(flows) : [];
          const src = l ? (typeof l.source === 'object' ? l.source.id : l.source) : null;
          const tgt = l ? (typeof l.target === 'object' ? l.target.id : l.target) : null;
          return {
            id: l && l.id,
            source: src,
            target: tgt,
            ports,
            flowKeys: keys.slice(0, 5),
            flowKeyCount: keys.length,
            packets: l && l.packets,
          };
        });
        console.log('[PCAP][ports] LinkListPCAP sample:', sample);
      } catch (e) {
      }
    }, [graphData]);
  
    const handleNodeLinkClick = (id, source, e) => {
        stopCameraSpin();
        handleNodeClick(id, source);
    };

    const openEndpointContextMenu = (id, e) => {
      try {
        if (e && typeof e.preventDefault === 'function') e.preventDefault();
        if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
      } catch (err) {
      }
      try {
        const raw = id != null ? String(id).trim() : '';
        if (!raw) return;
        const node = (graphData && Array.isArray(graphData.nodes))
          ? graphData.nodes.find((n) => n && String(n.id || '').trim() === raw)
          : null;
        const payloadNode = node || { id: raw };
        window.dispatchEvent(new CustomEvent('open-node-context-menu', {
          detail: {
            mouseX: e && e.clientX,
            mouseY: e && e.clientY,
            node: payloadNode,
            graphDataOverride: graphData,
          }
        }));
      } catch (err) {
      }
    };

    const handleConversationClick = (fromId, toId, e) => {
      try {
        if (e && typeof e.preventDefault === 'function') e.preventDefault();
        if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
      } catch (err) {
      }
      try {
        stopCameraSpin();
      } catch (e2) {
      }
      try {
        if (typeof onSelectConversation === 'function') {
          onSelectConversation(fromId, toId);
          return;
        }
      } catch (e3) {
      }
      // Fallback: behave like a node click on the From endpoint.
      handleNodeLinkClick(fromId, true, e);
    };

    const formatBytes = (bytes) => {
      if (!bytes) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
    };

    const toggleSort = (key) => {
      setSortState((prev) => {
        const p = prev && typeof prev === 'object' ? prev : { key: null, dir: 'asc' };
        if (p.key === key) return { key, dir: (p.dir === 'asc' ? 'desc' : 'asc') };
        return { key, dir: 'asc' };
      });
    };

    const sortIndicator = (key) => {
      if (!sortState || sortState.key !== key) return '';
      return sortState.dir === 'desc' ? ' 🔽' : ' 🔼';
    };

    const macDisplayByMac = buildMacDisplayMap();
    const idToLabel = buildIdToLabelMap();
    const idToIcon = buildIdToIconMap();
    const rows = Array.isArray(conversations) ? conversations : [];

    const sortedRows = React.useMemo(() => {
      const key = sortState && sortState.key ? String(sortState.key) : '';
      if (!key) return rows;
      const dir = sortState && String(sortState.dir || 'asc').toLowerCase() === 'desc' ? -1 : 1;
      const toNumber = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
      };
      const toText = (v) => {
        if (v == null) return '';
        return String(v).trim().toLowerCase();
      };

      const getProtocol = (row) => {
        const sourceId = row && row.from != null ? String(row.from).trim() : '';
        const targetId = row && row.to != null ? String(row.to).trim() : '';
        const rawProtocol = row && row.protocol != null ? String(row.protocol).trim() : '';
        const hasIpEndpoints = isIPv4(sourceId) || isIPv4(targetId);
        return (!hasIpEndpoints && (isMacAddress(sourceId) || isMacAddress(targetId)))
          ? (rawProtocol || 'L2')
          : (rawProtocol || 'IP');
      };

      const getPorts = (row) => {
        const fp = row && row.fromPort != null ? String(row.fromPort).trim() : '-';
        const tp = row && row.toPort != null ? String(row.toPort).trim() : '-';
        return ((fp && fp !== '-') || (tp && tp !== '-')) ? `${fp || '-'}:${tp || '-'}` : 'n/a';
      };

      const getValue = (row) => {
        if (!row) return '';
        if (key === 'from') return displayEndpoint(row.from, macDisplayByMac);
        if (key === 'to') return displayEndpoint(row.to, macDisplayByMac);
        if (key === 'fromMac') return formatMacWithOui(row.fromMac, macDisplayByMac);
        if (key === 'toMac') return formatMacWithOui(row.toMac, macDisplayByMac);
        if (key === 'protocol') return getProtocol(row);
        if (key === 'ports') return getPorts(row);
        if (key === 'bytes') return toNumber(row.bytesTotal);
        if (key === 'packets') return toNumber(row.packets);
        return '';
      };

      const next = rows.slice();
      next.sort((a, b) => {
        const va = getValue(a);
        const vb = getValue(b);
        if (key === 'bytes' || key === 'packets') {
          return (toNumber(va) - toNumber(vb)) * dir;
        }
        return toText(va).localeCompare(toText(vb)) * dir;
      });
      return next;
    }, [rows, sortState, macDisplayByMac, idToLabel, showNames, pcapShowOui, graphData]);

    return (
      <div className={classes.linkListContainer}>
        <table {...{ className: classes.table }} style={{ fontSize: '10px', lineHeight: '12px' }}>
          <thead>
            <tr>
              <th
                className={classes.tableHeader}
                onClick={() => toggleSort('from')}
                style={{ position: 'sticky', top: 0, backgroundColor: '#000', textAlign: 'left', padding: '4px', width: '18%', fontSize: '10px', cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', height: '20px', borderBottom: '1px solid gray' }}>From{sortIndicator('from')}</div>
              </th>
              <th
                className={classes.tableHeader}
                onClick={() => toggleSort('to')}
                style={{ position: 'sticky', top: 0, backgroundColor: '#000', textAlign: 'left', padding: '4px', width: '18%', fontSize: '10px', cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', height: '20px', borderBottom: '1px solid gray' }}>To{sortIndicator('to')}</div>
              </th>
              <th
                className={classes.tableHeader}
                onClick={() => toggleSort('fromMac')}
                style={{ position: 'sticky', top: 0, backgroundColor: '#000', textAlign: 'left', padding: '4px', width: '14%', fontSize: '10px', cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', height: '20px', borderBottom: '1px solid gray' }}>From MAC{sortIndicator('fromMac')}</div>
              </th>
              <th
                className={classes.tableHeader}
                onClick={() => toggleSort('toMac')}
                style={{ position: 'sticky', top: 0, backgroundColor: '#000', textAlign: 'left', padding: '4px', width: '14%', fontSize: '10px', cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', height: '20px', borderBottom: '1px solid gray' }}>To MAC{sortIndicator('toMac')}</div>
              </th>
              <th
                className={classes.tableHeader}
                onClick={() => toggleSort('protocol')}
                style={{ position: 'sticky', top: 0, backgroundColor: '#000', textAlign: 'left', padding: '4px', width: '10%', fontSize: '10px', cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', height: '20px', borderBottom: '1px solid gray' }}>Protocol{sortIndicator('protocol')}</div>
              </th>
              <th
                className={classes.tableHeader}
                onClick={() => toggleSort('ports')}
                style={{ position: 'sticky', top: 0, backgroundColor: '#000', textAlign: 'left', padding: '4px', width: '13%', fontSize: '10px', cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', height: '20px', borderBottom: '1px solid gray' }}>Ports{sortIndicator('ports')}</div>
              </th>
              <th
                className={classes.tableHeader}
                onClick={() => toggleSort('bytes')}
                style={{ position: 'sticky', top: 0, backgroundColor: '#000', textAlign: 'right', padding: '4px', width: '15%', fontSize: '10px', cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', height: '20px', borderBottom: '1px solid gray' }}>Bytes{sortIndicator('bytes')}</div>
              </th>
              <th
                className={classes.tableHeader}
                onClick={() => toggleSort('packets')}
                style={{ position: 'sticky', top: 0, backgroundColor: '#000', textAlign: 'center', padding: '4px', width: '8%', fontSize: '10px', cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '20px', borderBottom: '1px solid gray' }}>Packets{sortIndicator('packets')}</div>
              </th>
            </tr>
          </thead>
          <tbody className={classes.tableBody}>
            {sortedRows.map((row, index) => {
              const sourceId = row && row.from != null ? String(row.from).trim() : '';
              const targetId = row && row.to != null ? String(row.to).trim() : '';
              if (!sourceId || !targetId) return null;

              const sourceLabel = displayEndpoint(sourceId, macDisplayByMac);
              const targetLabel = displayEndpoint(targetId, macDisplayByMac);

              const sourceIcon = idToIcon && typeof idToIcon.get === 'function' ? idToIcon.get(sourceId) : '';
              const targetIcon = idToIcon && typeof idToIcon.get === 'function' ? idToIcon.get(targetId) : '';

              const fromMac = (row && row.fromMac != null) ? formatMacWithOui(String(row.fromMac).trim(), macDisplayByMac) : '-';
              const toMac = (row && row.toMac != null) ? formatMacWithOui(String(row.toMac).trim(), macDisplayByMac) : '-';

              const fp = row && row.fromPort != null ? String(row.fromPort).trim() : '-';
              const tp = row && row.toPort != null ? String(row.toPort).trim() : '-';
              const ports = ((fp && fp !== '-') || (tp && tp !== '-')) ? `${fp || '-'}:${tp || '-'}` : 'n/a';

              const bytesTotal = row && row.bytesTotal != null ? Number(row.bytesTotal) : 0;
              const packets = row && row.packets != null ? row.packets : '-';
              const rawProtocol = row && row.protocol != null ? String(row.protocol).trim() : '';
              const hasIpEndpoints = isIPv4(sourceId) || isIPv4(targetId);
              const protocol = (!hasIpEndpoints && (isMacAddress(sourceId) || isMacAddress(targetId)))
                ? (rawProtocol || 'L2')
                : (rawProtocol || 'IP');

              return (
                <tr className={classes.tableRow} key={`${sourceId}-${targetId}-${index}`}>
                  <td className={classes.tableCell} style={{ padding: '1px 4px', fontSize: '10px' }}>
                    <button
                      onClick={(e) => handleConversationClick(sourceId, targetId, e)}
                      onContextMenu={(e) => openEndpointContextMenu(sourceId, e)}
                      className={classes.linkButtonStyle}
                      style={{ fontSize: '10px', lineHeight: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      {sourceIcon ? (
                        <img
                          alt=""
                          src={sourceIcon}
                          style={{ width: '12px', height: '12px', borderRadius: '2px' }}
                          referrerPolicy="no-referrer"
                          crossOrigin="anonymous"
                          onError={(e) => {
                            try { e.currentTarget.style.display = 'none'; } catch (err) {}
                          }}
                        />
                      ) : null}
                      {sourceLabel}
                    </button>
                  </td>
                  <td className={classes.tableCell} style={{ padding: '1px 4px', fontSize: '10px' }}>
                    <button
                      onClick={(e) => handleConversationClick(sourceId, targetId, e)}
                      onContextMenu={(e) => openEndpointContextMenu(targetId, e)}
                      className={classes.linkButtonStyle}
                      style={{ fontSize: '10px', lineHeight: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      {targetIcon ? (
                        <img
                          alt=""
                          src={targetIcon}
                          style={{ width: '12px', height: '12px', borderRadius: '2px' }}
                          referrerPolicy="no-referrer"
                          crossOrigin="anonymous"
                          onError={(e) => {
                            try { e.currentTarget.style.display = 'none'; } catch (err) {}
                          }}
                        />
                      ) : null}
                      {targetLabel}
                    </button>
                  </td>
                  <td className={classes.tableCell} style={{ padding: '1px 4px', fontSize: '10px' }}>
                    <span className={classes.linkButtonStyle} style={{ color: 'rgba(255,255,255,0.85)', fontSize: '10px', lineHeight: '12px' }}>
                      {fromMac}
                    </span>
                  </td>
                  <td className={classes.tableCell} style={{ padding: '1px 4px', fontSize: '10px' }}>
                    <span className={classes.linkButtonStyle} style={{ color: 'rgba(255,255,255,0.85)', fontSize: '10px', lineHeight: '12px' }}>
                      {toMac}
                    </span>
                  </td>
                  <td className={classes.tableCell} style={{ padding: '1px 4px', fontSize: '10px' }}>
                    <span className={classes.linkButtonStyle} style={{ color: '#88CCFF', fontSize: '10px', lineHeight: '12px' }}>
                      {protocol}
                    </span>
                  </td>
                  <td className={classes.tableCell} style={{ padding: '1px 4px', fontSize: '10px' }}>
                    <span className={classes.linkButtonStyle} style={{ fontSize: '10px', lineHeight: '12px' }}>
                      {ports}
                    </span>
                  </td>
                  <td className={classes.tableCellNumber} style={{ padding: '1px 4px', textAlign: 'right', fontSize: '10px' }}>
                    <span className={classes.linkButtonStyle} style={{ color: 'RGB(0, 255, 0)', fontSize: '10px', lineHeight: '12px' }}>
                      {formatBytes(bytesTotal)}
                    </span>
                  </td>
                  <td className={classes.tableCellNumber} style={{ padding: '1px 4px', textAlign: 'center', fontSize: '10px' }}>
                    <span className={classes.linkButtonStyle} style={{ fontSize: '10px', lineHeight: '12px' }}>
                      {packets}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };